import { APP_POOL_NODE_GROUP_ID_PATTERN as APP_POOL_NODE_GROUP_ID_PATTERN$1, appPoolNodeGroupsSchema } from "./app-pool-node-group.js";
import { ObservabilityStatusSchema } from "./observability.schemas.js";
import { DoorPullReportSchema } from "./door-pull.schemas.js";
import { LOWERCASED_UUID_PATTERN } from "./deployment-config.js";
import { blobBindingsFileSchema } from "./blob-bindings.js";
import { CustomerChannelDescriptorSchema } from "./desired-state.schemas.js";
import { liveNodePoolSchema } from "./live-node-pool.js";
import { APP_POOL_CONCURRENCY_MAX, APP_POOL_MAX_POOLS_PER_APP, APP_POOL_NODES_MAX, APP_POOL_PLACEMENT_STATES, APP_POOL_WORKLOADS, AppPoolTiersStatusSchema, BLOB_BUCKET_DELETE_AFTER_DAYS_MAX, COMPUTE_RESOLUTION_MAX, FIXTURE_ID_PATTERN, SATELLITE_PHASES, UNSATISFIED_DEMAND_MAX, appAdoptionStateSchema, appPoolNameSchema, appPoolRegionSchema, blobBucketNameSchema, channelShareDecisionStateSchema, computeResolutionSchema, dataLocationSchema, unsatisfiedDemandSchema } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/status-report.schemas.ts
/**
* Zod schemas for the status-report endpoint (DP operator → CP).
*
* POST /v1/releases/data-planes/:dpId/status
*
* Used by CP release-engine to validate inbound reports; types in
* `status-report.ts` derive from these via `z.infer`.
*/
const HealthSchema = z.enum([
	"healthy",
	"degraded",
	"unhealthy"
]);
const ServiceStatusSchema = z.object({
	version: z.string(),
	health: HealthSchema,
	readyPods: z.number().int().nonnegative(),
	desiredPods: z.number().int().nonnegative()
}).strict();
const EnvelopeErrorSchema = z.object({
	/**
	* OPTIONAL as of channel-version-routing-restore §6.1a. The three ENVELOPE-LEVEL
	* kinds below (`cp.unreachable`, `envelope.schema.invalid`, `stream-spec-invalid`)
	* are operator-emitted and have no app scope at all, so there is no `appId` to
	* report; every app-scoped kind still sets it. This is the widening
	* `reconciler.ts` always intended — its own comment promised "a follow-up
	* contracts patch will widen `kind` and make `appId` optional so the cast can be
	* removed", and the three double-casts are gone as of this change.
	*
	* SYS-020.2 permits it explicitly ("Widening (adding to a union, making required
	* optional) is allowed") — it is the forbidden direction, optional→required, that
	* clause rules out.
	*
	* Consumers that INDEX by `appId` must presence-guard. `fixture-version-syncer.ts`
	* already does the right thing by skipping an entry whose `appId` names no app: an
	* envelope-level error degrades no single app, so it must not raise an
	* `app_degradations` row.
	*/
	appId: z.string().min(1).optional(),
	/**
	* channel-version-routing-restore Q8/Q9 — two additions, both about the ROUTING
	* consequences of envelope data rather than about a malformed manifest, which is
	* why neither is folded into `fixture-manifest-invalid`: the manifest parses
	* fine, the cross-version routing data does not, and the CP-side fix differs.
	*
	*  - `channel-binding-ambiguous`: two DISTINCT `versionSlug`s of one fixture
	*    declare the same channel. CP should never emit that (a channel pins exactly
	*    one version), so it is an envelope-data defect. No routing table can satisfy
	*    it; the operator emits no rule for that channel and the owner fall-through
	*    serves it.
	*  - `route-rule-skipped`: the operator declined to emit a routing rule the
	*    envelope asked for — an invalid channel name, a value too long for a
	*    Gateway-API header match, an org default naming an inactive version, or the
	*    per-route rule/match budget being exhausted. One kind for "we could not
	*    express this rule", discriminated by `errors[].instancePath` (a JSON pointer
	*    into the DesiredStateEnvelope) and a leading reason token in
	*    `errors[].message`.
	*
	* `instancePath` on these two kinds points into the ENVELOPE, not into a fixture
	* manifest as the Ajv-derived kinds do (`ajvErrorToEnvelopeError`). The message
	* says so.
	*
	* ---
	*
	* §6.1a — the three ENVELOPE-LEVEL kinds. Unlike every kind above, these are
	* operator-emitted, carry NO app scope (hence no `appId`), and describe a failure
	* of the envelope-fetch or envelope-parse machinery rather than of any one app's
	* declared content. They are listed here because the operator has emitted all
	* three since before this enum was closed, forwarding them past the type with a
	* double-cast on the belief that "CP's status-report consumer is permissive (Ajv
	* `additionalProperties: true`)".
	*
	* That belief was STALE, and the consequence was live: CP validates the whole
	* report against this schema, so each of these emissions 400'd the ENTIRE POST and
	* blacked out all status reporting for that DP. `stream-spec-invalid` was the worst
	* — it rides a SUCCESSFUL cycle's report, so one malformed StreamSpec in an app
	* manifest silently killed a DP's health, per-app status, `dedicatedNodePools` and
	* `computeUnitInUseNodeTypes` every cycle until someone fixed the manifest, with
	* the operator log the only place the cause appeared.
	*
	*  - `cp.unreachable`: the desired-state fetch failed and the operator is running
	*    on cached state, or skipping the cycle when it has none.
	*  - `envelope.schema.invalid`: the desired-state body CP served failed
	*    `DesiredStateEnvelopeSchema` (`desired-state-envelope.schemas.ts`, #1009). The
	*    operator program does not reconcile that body. It reconciles its last accepted
	*    envelope, or skips the cycle when it has none. The operator program sends one
	*    entry with no `appId` on each such cycle. `errors[0]` is a summary at
	*    `instancePath: ''` that names the ETag of the envelope it serves, or says it has
	*    none. At most 20 schema issues follow, each with a JSON Pointer into the body.
	*  - `stream-spec-invalid`: one `StreamSpec` in the envelope failed schema
	*    validation and was skipped; the valid remainder still reconciles.
	*
	* ---
	*
	* #2839 and #2257 (defect 2), plan 2026-10-20-dp-bugs-batch-j: one app-scoped kind for a
	* fixture whose database credential the operator program could not make in this cycle.
	*
	*  - `fixture-credential-unavailable`: the operator program could not make the credential of
	*    one fixture that declares `resources.database`. Its `<app>--<fixture>--db-credentials`
	*    Secret can be absent, and its pods then wait in `CreateContainerConfigError`. The entry
	*    sets `appId` and `fixtureId`. `errors[].message` starts with one reason token:
	*    - `reconcile-failed`: the db-credential reconciler failed for this fixture;
	*    - `reconcile-crashed`: the db-credential reconciler threw for the whole app;
	*    - `reconciler-unwired`: this data plane has no db-credential reconciler, because
	*      `DP_TENANCY_ADMIN_DATABASE_URL` is unset. This cause does not clear by itself: the
	*      operator program needs a restart with the variable set.
	*
	* Release order: CP before DP. An older CP refuses the whole report that carries this
	* kind (the closed-set tests in `status-report.schemas.test.ts`), and the operator program drops
	* a refused report and sends the next one a cycle later. Ship the CP that accepts before
	* the DP that emits.
	*/
	kind: z.enum([
		"app-manifest-invalid",
		"fixture-manifest-invalid",
		"channel-binding-ambiguous",
		"route-rule-skipped",
		"cp.unreachable",
		"envelope.schema.invalid",
		"stream-spec-invalid",
		"fixture-credential-unavailable"
	]),
	fixtureId: z.string().min(1).optional(),
	fixtureVersion: z.string().min(1).optional(),
	errors: z.array(z.object({
		instancePath: z.string(),
		message: z.string()
	}).strict())
}).strict();
const FixtureServiceStatusSchema = z.object({
	health: HealthSchema,
	readyPods: z.number().int().nonnegative(),
	desiredPods: z.number().int().nonnegative(),
	/**
	* custom-autoscale WS-4 [D3] — the service's machine type (from manifest `svc.nodeType`) for per-pool
	* cost attribution; `null` on the base pool. Consumed by WS-6's CP-side per-service `captureScaleEvents`
	* diff (the per-service `versions[v].services[s]` map is the diff input, not the version-level sum).
	*/
	nodeType: z.string().min(1).nullable().optional()
}).strict();
const FixtureJobRunSchema = z.object({
	name: z.string().min(1),
	lastRunAt: z.string().min(1).optional(),
	lastStatus: z.enum([
		"succeeded",
		"failed",
		"running"
	]).optional(),
	failureCount: z.number().int().nonnegative().optional()
}).strict();
const StatusReportDashboardSchema = z.object({
	name: z.string().min(1),
	title: z.string().min(1),
	panelsJson: z.string()
}).strict();
const FixtureRecordingRuleSchema = z.record(z.string(), z.unknown());
const FixtureAlertingRuleSchema = z.record(z.string(), z.unknown());
const FixtureVersionStatusSchema = z.object({
	version: z.string().min(1),
	replicaSet: z.enum([
		"ready",
		"progressing",
		"failed"
	]),
	readyReplicas: z.number().int().nonnegative(),
	desiredReplicas: z.number().int().nonnegative(),
	lastRolloutAt: z.string().min(1),
	migrationApplied: z.boolean(),
	channels: z.array(z.string()),
	isOwner: z.boolean(),
	isDefault: z.boolean(),
	ringName: z.enum([
		"stable",
		"beta",
		"alpha",
		"edge"
	]).optional(),
	health: HealthSchema,
	services: z.record(z.string(), FixtureServiceStatusSchema).optional(),
	jobs: z.array(FixtureJobRunSchema).optional(),
	metricsRules: z.array(FixtureRecordingRuleSchema).optional(),
	metricsAlerts: z.array(FixtureAlertingRuleSchema).optional(),
	dashboards: z.array(StatusReportDashboardSchema).optional()
}).strict();
const FixtureStatusSchema = z.object({ versions: z.record(z.string(), FixtureVersionStatusSchema) }).strict();
const AppStatusSchema = z.object({
	appVersion: z.string().min(1),
	health: HealthSchema,
	fixtures: z.record(z.string(), FixtureStatusSchema)
}).strict();
const MigrationStatusSchema = z.object({
	service: z.string().min(1),
	migrationId: z.string().min(1),
	completedAt: z.string().min(1),
	durationMs: z.number().int().nonnegative()
}).strict();
/**
* One observed container RESTART on a pod this operator manages
* (apps-MCP v2 spine V-6, seam S5 — a slice of #825).
*
* ## What a record IS, precisely
*
* A record exists **iff** a container restarted: it is emitted from
* `containerStatuses[].lastState.terminated` — the exit of the run that ENDED —
* paired with `restartCount` as it stands AFTER that restart. So `exitCode` and
* `reason` describe the **previous** run, and `restartCountAfter` is the count
* observed alongside it. A container's *current* `state.terminated` (a completed
* Job pod, a final Error before deletion) is deliberately NOT a record: it is not
* flapping, the report already carries `jobs[]` for job outcomes, and admitting it
* would make the dedup key `(podUid, containerName, restartCountAfter)` ambiguous
* between "the restart at count N" and "the final exit at count N".
*
* ## Producer-side dedup, and why the CONSUMER still needs a unique key
*
* The operator emits each `(podUid, containerName, restartCountAfter)` at most
* once per process lifetime (`pod-lifecycle-reporter.ts`). That memory is
* in-process: an operator restart re-emits every live pod's current `lastState`
* exactly once. ⇒ CP's ingest MUST be idempotent on that triple — it is, via a
* UNIQUE index (`release.app_restart_events`, migration 0013).
*
* ## ⛔ `reason` is `z.string()`, NOT an enum — and this is a safety decision
*
* The kubelet's termination-reason vocabulary is OPEN-WORLD (`OOMKilled`, `Error`,
* `Completed`, `ContainerStatusUnknown`, `DeadlineExceeded`, plus runtime-specific
* values). A closed `z.enum` inside this `.strict()` report is exactly the shape
* that took a DP's status reporting down: see this file's `EnvelopeErrorSchema`
* docblock (`kind` was closed, the operator emitted a value CP did not know, and
* the whole POST 400'd every cycle until a human intervened). The same reasoning
* that makes `AppHistoryState.health` a bare `string` in `@zion/sdk-contracts`
* applies twice over here: this is a LEDGER of values already written to disk,
* possibly by an older producer.
*
* ## ⛔ `exitCode` carries no range bound
*
* `V1ContainerStateTerminated.exitCode` is a plain int32 in the Kubernetes API.
* `137` (SIGKILL) and `143` (SIGTERM) are the everyday values; a runtime is free
* to report others, including negatives. A tight bound here would 400 the ENTIRE
* status report over one odd exit code. `.int()` is the only honest constraint.
*
* ## `restartCountAfter` IS bounded — `.positive()`
*
* `>= 1` holds by construction (a record exists only because a restart happened),
* so this is a real invariant rather than a guess. The producer parses its own
* output against this schema before sending (see the reporter), so a violation
* drops ONE record instead of 400-ing the report.
*
* `at` is `z.string().min(1)`, matching every other instant in this file
* (`reportedAt`, `lastRolloutAt`) rather than `z.string().datetime()`. The producer
* normalises it to a canonical UTC ISO-8601 instant.
*
* ## ⛔⛔ EVERY FIELD IS BOUNDED, AND THE BOUNDS ARE AN AVAILABILITY CONTROL (R-30.2)
*
* `release-engine`'s express body limit is the **measured default 100 kB** (§0.2(g)), so an
* unbounded record makes the whole report a 413 — which never reaches this schema
* at all, so none of the defences above are in that path. Each bound below is the
* PRODUCER'S REAL DOMAIN, cited, so it narrows nothing legitimate:
*  - `appId` / `fixtureId` / `containerName` ⇒ **63** — a Kubernetes label value and
*    a DNS-1123 label are both capped there, and all three are read off labels.
*  - `podName` ⇒ **253** — a DNS-1123 SUBDOMAIN (pod names are generated with a
*    suffix, so they are longer than a label).
*  - `reason` ⇒ **256** — see the untrusted-text note below.
*  - `podUid` ⇒ **`z.uuid()`**, see the cardinality note below.
*  - `at` ⇒ **64** — see its own docblock. It was the ONE unbounded string here, and
*    its absence falsified the operator's carry-always-drains liveness proof.
* A record therefore has a BOUNDED size rather than an unbounded one, and
* {@link POD_LIFECYCLE_MAX} bounds the array on top.
*
* ⚠️ **Bounded is not the same as small, and this docblock used to conflate them**
* (corrected under R-71). Measured, compact JSON: a realistic record is **249 B**, but a
* record with every field at its own `.max()` is **943 B** — 3.8×. So these bounds prove
* the record cannot be *unbounded*; they do not prove the ARRAY fits a body budget. See
* {@link POD_LIFECYCLE_MAX} for what the count does and does not guarantee, and note that
* the byte-level bound lives in the PRODUCER (it slices on a serialized byte budget —
* only the producer can measure bytes; zod cannot).
*
* ## ⛔ `podUid` is `z.uuid()` — it is the only CARDINALITY bound on the table
*
* `release.app_restart_events`' UNIQUE index `(data_plane_id, pod_uid, container_name,
* restart_count_after)` is the only thing bounding row growth. Kubernetes uids are
* ALWAYS uuids, so accepting an arbitrary string would key that index on an unbounded
* value space: a DP varying `podUid` per record inserts unboundedly many distinct
* rows, which is the storage-growth vector §10's volume row otherwise assumes away.
* The producer reads a real k8s uid, so this narrows nothing legitimate.
*
* ## ⛔ `fixtureId` carries the DNS-1123 pattern so the WRITE domain equals the READ domain
*
* The read filter is `FIXTURE_ID_PATTERN` (`@zion/sdk-contracts`). A stored value
* outside it would be returned unfiltered and be **permanently unreachable by the
* advertised filter** — a lying surface of exactly the C-4 class. Applying the same
* pattern here cannot drop a legitimate record: `fixtureId` is a k8s object-name
* component (the operator builds Deployment/Service names from it), so a value
* outside DNS-1123 could never have produced a running pod to observe.
*
* ## ⛔ `reason` is UNTRUSTED DP-SUPPLIED TEXT, bounded, and rendered into an agent's context
*
* It is the one open-vocabulary field here, and it is the one field an agent is told
* to reason FROM — so it is both a body-budget consumer and a context-flooding /
* instruction-injection surface on an MCP read. ⛔ Do not branch on its value
* anywhere in CP; treat it as a display string. `.max(256)` is the bound.
*
* ## ⛔ TRUST BOUNDARY — `appId` is DP-ASSERTED and corroborated by nothing here (R-31)
*
* `data_plane_id` **is** authenticated: `OperatorMtlsGuard` requires the XFCC
* certificate CN to EQUAL the URL `:dpId`, and `ApprovedDpIdGuard` requires the
* `(dpId, fingerprint)` pair in `dp_operator_certs`. The **app identity is not**: it
* is a pod label forwarded verbatim, and this schema constrains only its shape. A
* compromised or buggy operator could assert an app it does not host.
* ⇒ **CP fences that at INGEST** (`captureRestartEvents`, R-31.1) against a CP-side
* hosting fact, with a counted, logged drop. The fence is not in this schema and must
* not be assumed from it.
*/
const PodLifecycleRecordSchema = z.object({
	/** The app SLUG — the `zion.witness.ai/app` label value, matching `dp_fixture_status.app_id` (G1).
	*  ⛔ DP-ASSERTED — fenced at ingest, not here. See the trust-boundary note above. */
	appId: z.string().min(1).max(63),
	/** The fixture id — `zion.witness.ai/fixture`, or the bare `fixture` label on worker pods (#971).
	*  The pattern makes the write domain equal the read filter's domain. */
	fixtureId: z.string().regex(FIXTURE_ID_PATTERN),
	/** `pod.metadata.uid` — the identity that survives a name reuse and dies with the pod.
	*  ⛔ `z.uuid()`: the table's only cardinality bound keys on it. */
	podUid: z.uuid(),
	/** DNS-1123 SUBDOMAIN — pod names carry a generated suffix. */
	podName: z.string().min(1).max(253),
	/**
	* The container's name. Unique within a pod ACROSS `containers` and
	* `initContainers` (Kubernetes enforces it), which is why records from
	* `initContainerStatuses` — the native sidecars: signer, nats-leaf, llm-proxy —
	* need no `kind` discriminator to stay distinguishable.
	*/
	containerName: z.string().min(1).max(63),
	/** Exit status of the run that ENDED. See the docblock on why there is no range bound. */
	exitCode: z.number().int(),
	/** Kubelet's brief reason for that termination. Open vocabulary, UNTRUSTED, bounded. */
	reason: z.string().min(1).max(256),
	/**
	* When that run finished, as a canonical UTC ISO-8601 instant (the kubelet's clock).
	*
	* ⛔ **`.max(64)` IS AN AVAILABILITY CONTROL, AND ITS ABSENCE FALSIFIED A LIVENESS
	* PROOF THAT WAS ALREADY SHIPPED.** Until this bound existed `at` was the ONE string
	* in this record with no `.max()`, while the docblock above claimed *"a record
	* therefore has a BOUNDED size"*. The operator's reporter argues from that claim:
	* *"the largest record the wire admits is 903 B and `2 + 903 <=
	* POD_LIFECYCLE_MAX_BYTES` always holds ⇒ a non-empty candidate list can never yield
	* an empty slice, so the carry always drains"* (`pod-lifecycle-reporter.ts:343`).
	* ⚠️ That quotation is VERBATIM and its number is now STALE — the true maximal is
	* **943 B** (the operator measured 903 from a fixture whose `at` was a realistic
	* 24-char instant, which is the only kind of fixture an unbounded `at` allowed). The
	* conclusion survives at 943 and the proof is now sound, but the three citations in
	* `pod-lifecycle-reporter.ts` still say 903 and are tracked in
	* `escalations/dev-ctr2.md` — that file is outside this package. With
	* `at` unbounded that conclusion is FALSE: one record over 40 kB makes the
	* longest-prefix slice EMPTY, nothing is emitted, the record carries forever, and
	* `podLifecycle` **silently stops reporting for that data plane permanently**. R-31
	* puts a buggy or compromised operator in the threat model — which is exactly why the
	* other six fields were bounded and why this one had to be.
	*
	* 64 is generous: an ISO-8601 instant at microsecond precision with an offset is ~32
	* chars, so this narrows nothing a kubelet can legitimately produce.
	*
	* ⚠️ It is a LENGTH bound, not a parseability one. `at: 'banana'` is still
	* representable — the schema does not claim the value is a parseable instant, and
	* anything that must parse it has to handle failure itself.
	*/
	at: z.string().min(1).max(64),
	/** `restartCount` observed alongside the termination. `>= 1` by construction. */
	restartCountAfter: z.number().int().positive()
}).strict();
/**
* Hard cap on the NUMBER of `StatusReport.podLifecycle` records (apps-MCP v2 R-30.1,
* re-scoped by **R-71**).
*
* ## ⛔ THIS IS A COUNT BOUND. IT IS NOT A PROOF THAT THE BODY FITS.
*
* Measured (compact JSON, 2026-08-07), against release-engine's **measured 100 kB express
* default** (§0.2(g)):
*
* | record | size | × 200 |
* |---|---|---|
* | realistic (`witness-search`/`gateway`/`OOMKilled`) | **249 B** | **≈50 kB** — the design case, fits |
* | every field at its own `.max()` | **943 B** | **≈189 kB** — ⛔ 1.9× OVER, before the rest of the report |
*
* ⚠️ An earlier version of this docblock called ≈46 kB the "worst case" and claimed a
* "PROVEN maximum size". Both were derived from the *typical* record while the schema
* bounds admit the *maximal* one — a 3.8× gap. The claim is corrected rather than deleted
* because the overclaim is the interesting part: **once every field is `.max()`ed, the
* maximal record is reachable by construction**, and R-31 puts a compromised or buggy
* operator in the threat model, so "no real producer emits 943 B records" is exactly the
* assumption R-30 exists to stop relying on.
*
* ## Why the number stays 200 rather than shrinking
*
* A COUNT cap cannot express "the body fits" when record size varies 3.8× (R-71): 110
* would leave **670 bytes** for the entire rest of the report (the apps map, instances,
* envelopeErrors, node pools), and a count that genuinely reserves room is **44** —
* punitive against the 249 B records a real producer sends. ⇒ 200 is kept as what it
* honestly is: **a guard against an UNBOUNDED array**, which is worth having on its own.
*
* ⇒ ⛔ **The byte-level bound lives in the PRODUCER**, which slices on a serialized byte
* budget before sending — only the producer can measure bytes; zod cannot express "the
* encoded array is under N kB". The producer carries the remainder to the next report, so
* both bounds cost drain cycles and lose no data.
*
* ⛔ Exported so the PRODUCER's count slice and the CONSUMER's `.max()` are the same
* literal — two copies of a budget is how one of them drifts past the other. It must not
* move without moving the operator's slice in the same change.
*/
const POD_LIFECYCLE_MAX = 200;
/**
* app-pools WS-2. The cap on `appPools[]`, one row per declared pool on the whole data plane.
*
* This is an availability backstop and not a truncator, the posture `POD_LIFECYCLE_MAX`,
* `COMPUTE_RESOLUTION_MAX` and `UNSATISFIED_DEMAND_MAX` all take on this schema. An over-cap array
* is rejected and not trimmed. `StatusReportSchema` is `.strict()` end to end, so that rejection
* fails the whole report for every app on the data plane. That is the right trade for a control
* channel, because a DP that can force unbounded CP-side persistence is the worse failure. It is
* also what makes WS-4's producer-side slice mandatory instead of advisory.
*
* Exported so the producer's slice and this `.max()` are the same literal.
*
* This figure is not `APP_POOL_NODE_GROUP_MAX` and must not be collapsed into it. The two arrays
* sit at different grains. A node group is per `(appKey, orgId, workload, nodeType)`, so one
* out-of-mesh pool fans out to one group per resident org. This array holds one row per declared
* pool whatever its placement, including a pool with no group at all (`unschedulable`,
* `provider-local`). Neither bound implies the other.
*/
const APP_POOL_REPORTED_STATUS_MAX = 200;
/**
* Cap on the free-text refusal reason. Bounded because it rides a `.strict()` wire and is
* rendered in the console; the operator truncates rather than omitting.
*
* Exported for the reason `APP_POOL_REPORTED_STATUS_MAX` is: the producer's slice and this
* `.max()` have to be the same literal. `reportAppPools` slices the reason to this length, and the
* message it slices is an operator-internal exception string with no bound of its own. A second
* copy of the figure on the producer's side would let the two drift. The drifting direction is the
* one where `StatusReportSchema` rejects the whole report and blacks out that data plane's
* status reporting every cycle (app-pools R-054, SCA-4).
*/
const APP_POOL_UNSCHEDULABLE_REASON_MAX_LENGTH = 512;
/**
* One pool's live status, as the DP reports it: where it can run, and the numbers in force.
*
* Produced by WS-4, one entry per declared pool on the data plane whatever its placement. Consumed
* by WS-3, which persists the array and renders it. Rides {@link StatusReport.appPools}.
*
* A conforming object parses to itself, and anything else is a `ZodError`. `.strict()`, so an
* unknown key is a parse error. Pure.
*
* `groupId` and `nodeType` are nullable, and their null carries meaning. On `unschedulable` and
* `provider-local` there is no group and no machine type to name. A placeholder there would put a
* plausible-looking machine type in front of an operator diagnosing why nothing runs.
*/
const AppPoolReportedStatusSchema = z.object({
	/** The app that declares the pool. */
	appKey: z.string().min(1),
	/** The pool's name, its key in `app.json` `pools{}`. */
	pool: z.string().min(1),
	/** Which containment. Decides whether the pool's node group is per org (chain D hop 1). */
	workload: z.enum(APP_POOL_WORKLOADS),
	/** The node group serving this pool, or `null` when there is none. See the schema docblock.
	*  One pool maps to several groups when it runs out-of-mesh for several orgs. This member names
	*  the group for the reported row's own resolution, and `appPoolNodeGroups` is the full set. */
	groupId: z.string().min(1).nullable(),
	/** The resolved machine type, or `null` when placement did not resolve. */
	nodeType: z.string().min(1).nullable(),
	/** Where the pool's work can run: `APP_POOL_PLACEMENT_STATES` from `@zion/sdk-contracts`, the one
	*  declaration the operator's `describe` route shares (DES-10). */
	placement: z.enum(APP_POOL_PLACEMENT_STATES),
	/** Why the pool cannot take work, or `null` when it can. Populated whenever the pool cannot
	*  take work, which is not the same as `placement === 'unschedulable'`: a `provider-local` pool
	*  carries the reason its compute ceiling is unknown until the local node read succeeds, and
	*  admission refuses every submit to it naming that reason (R-054, ACA-12). No `.refine` ties
	*  the two members, because refusing a mismatched pair would take one data plane's whole report
	*  down to hide a string. */
	unschedulableReason: z.string().min(1).max(512).nullable(),
	/**
	* The concurrency in force, after the pool's function knob was evaluated and clamped, and not
	* the declared number. This is why these three knobs are reported at all. A pool whose
	* `concurrency` is a `{ algorithm, max }` knob has no declared figure to show. The compute
	* view has to show what is running instead of what the manifest asked for.
	*
	* The manifest maxima, `APP_POOL_CONCURRENCY_MAX` and its siblings, are left out as bounds
	* here on purpose. A post-clamp value above its cap is a compiler defect. Enforcing the cap on
	* this `.strict()` whole-report parse would take a whole data plane's status report down for
	* one app's arithmetic. `unsatisfiedDemand` shows the cost: its `.max(UNSATISFIED_DEMAND_MAX)`
	* fails the whole report on an over-cap array, and the schema accepts that cost there only
	* because an unbounded array is the worse failure and the producer slices first. A clamped
	* scalar has no such reason. The report is a measurement, and the clamp is enforced where the
	* clamp happens.
	*/
	concurrency: z.number().int().nonnegative(),
	/** The warm floor in force, post-knob and post-clamp. See `concurrency` on the absent bound. */
	warmFloor: z.number().int().nonnegative(),
	/** The node ceiling in force, post-knob and post-clamp. See `concurrency` on the absent bound. */
	maxNodes: z.number().int().nonnegative(),
	/** Enqueued runs waiting for this pool, at report time. A sample and not a total. */
	depth: z.number().int().nonnegative(),
	/** Attempts running for this pool, at report time. */
	inFlight: z.number().int().nonnegative(),
	/** Standing members in force (R-009 c): `concurrency` per pool, or one per resident org for an
	*  out-of-mesh `per-tenant` pool; 0 for a per-attempt pool. */
	members: z.number().int().min(0),
	/**
	* The pool's declared `region` from `app.json`, or `null` for a home pool
	* (2026-09-29-gpu-and-regional-app-pools R-051). The operator (WS-9) copies it from the pool
	* declaration whatever the placement, so a regional pool refused `region-not-enabled`, which has
	* no group and a `null` `groupId`, still names its region. site-admin-api (WS-6) fills
	* `appPoolAdminRowSchema.region` from this member first and from the node group's `region` only
	* when this member is `null` or absent.
	*
	* A value matches `APP_POOL_REGION_PATTERN` (`us-east4`); `''`, `US-EAST4`, a one-character or
	* 32-character name, or a non-string is a `ZodError` that fails the whole report. The publish
	* doors check the declared region against the same pattern, so a real producer never trips it.
	*
	* Optional as well as nullable, by SYS-020.3: an operator built before R-051 omits the member,
	* and `StatusReportSchema` is `.strict()` end to end, so a required member would refuse every
	* report from every data plane not yet upgraded. Absent and `null` mean the same thing, no
	* declared region; a producer on this version writes `null` for a home pool. The admin row's
	* member is required and nullable, because the CP always fills it.
	*/
	region: appPoolRegionSchema.nullable().optional(),
	/**
	* The status of the pool's `PoolTiers` object (2026-10-24-pool-machine-generations R-019): one
	* `AppPoolTiersStatusSchema` from `@zion/sdk-contracts`, the shape the operator's tier
	* controller (WS-2b) writes into the object's status. Present only on a pool with `tiers` and
	* from an operator that reports it.
	*
	* Optional and not nullable, by SYS-020.3 and DEF-9: an operator built before R-019 omits the
	* member, and `StatusReportSchema` is `.strict()` end to end, so a required member would refuse
	* every report from every data plane not yet upgraded. A producer with no object omits the key;
	* `null` is a `ZodError`. A value that breaks `AppPoolTiersStatusSchema` fails the whole report,
	* as every other member does. site-admin-api (WS-2e) copies the value into
	* `appPoolAdminRowSchema.poolTiers` and writes `null` when the member is absent.
	*/
	poolTiers: AppPoolTiersStatusSchema.optional()
}).strict();
/**
* The most `PoolTiers` objects one pool reports: 100, `APP_POOL_NODE_GROUP_MAX`, the bound the
* slice's `residentOrgIds` carries for the same set of orgs. An out-of-mesh pool has one object per
* resident org, and an in-mesh pool has one. The producer slices to it, by `orgId` in ascending
* order.
*/
const APP_POOL_TIERS_STATUS_MAX = 100;
/**
* The `PoolTiers` objects of one pool: 1 to {@link APP_POOL_TIERS_STATUS_MAX} entries of
* {@link AppPoolTiersStatusSchema}. An empty list is refused; a producer with no object omits the
* list. Pure.
*/
const AppPoolTiersStatusListSchema = z.array(AppPoolTiersStatusSchema).min(1).max(APP_POOL_TIERS_STATUS_MAX);
/**
* private-release-channels R-010. Most decisions one status report may carry in
* `channelShareDecisions[]`. An over-cap array is refused, not trimmed, and `StatusReportSchema`
* is strict end to end, so the refusal fails the whole report: the operator's producer-side paging
* (WS-4, one page per report) is mandatory. Size: one decision is 215 bytes in compact JSON with a
* millisecond `decidedAt`, and 430 bytes at every field's longest form, so a full page is 4.3 kB to
* 8.6 kB of the 100 kB report body. Exported so the producer's page size and this `.max()` are the
* same literal.
*/
const CHANNEL_SHARE_DECISIONS_MAX = 20;
/**
* private-release-channels R-048. The most capability names one status report may carry, and the
* grammar of one name: a lowercase word of 2 to 40 characters with digits and hyphens. The
* stage-1 operator's entry is `PRIVATE_CHANNELS_CAPABILITY`.
*/
const STATUS_REPORT_CAPABILITIES_MAX = 16;
const CAPABILITY_PATTERN = /^[a-z][a-z0-9-]{1,39}$/;
const PRIVATE_CHANNELS_CAPABILITY = "private-channels";
/**
* 2026-09-29-gpu-and-regional-app-pools R-063. The capability name an operator reports in
* `StatusReport.capabilities` when it honours a pool's `region`: it resolves the pool against the
* satellite's catalog in `desired.satellites`, compiles its node group with `region`, and hands each
* attempt to that region's satellite. The stage-3 home operator (WS-10) sends it.
*
* The reader is release-engine's desired-state projection (WS-8). It withholds every pool that
* carries `region` from a data plane whose last status report lacks this entry, and records the
* refusal on the app's desired state. An operator built before R-063 would compile a regional pool
* onto a home node group with no error, so absence means "cannot honour `region`", never "unknown".
*
* The value is the literal `'regional-pools'`, inside `CAPABILITY_PATTERN`. Both ends import this
* constant; neither spells the string. Pure data, no side effects.
*/
const REGIONAL_POOLS_CAPABILITY = "regional-pools";
/**
* 2026-10-13-blob-buckets R-104 item 4. The capability name an operator program reports in
* `StatusReport.capabilities` when it handles the desired-state member
* `AppDesiredState.claimCheckBindings`: it parses the member, renders the claim-check bindings file
* into its own ConfigMap, and mounts it at `CLAIM_CHECK_BINDINGS_MOUNT_PATH` with
* `CLAIM_CHECK_BINDINGS_FILE_ENV` on every pod of an app with `platform.eventBus` (fixtures, pool
* pods and worker pods). The stage 6 operator program (WS-57) sends it.
*
* The reader is release-engine's desired-state projection (WS-58). It writes `claimCheckBindings`
* only for a data plane whose last status report, kept in `release.dp_live_status.capabilities`,
* names this entry, as it gates private pins on {@link PRIVATE_CHANNELS_CAPABILITY}. An operator
* program built before R-104 renders no claim-check file, so its app pods keep the legacy
* claim-check store. Absence means "renders no claim-check file", never "unknown".
*
* The value is the literal `'blob-claimcheck'`, inside `CAPABILITY_PATTERN`. Both ends import this
* constant; neither spells the string. Pure data, no side effects.
*/
const BLOB_CLAIMCHECK_CAPABILITY = "blob-claimcheck";
/**
* #4416. The capability name an operator program reports in `StatusReport.capabilities` when it
* handles per-version declarations: it writes each `declaredPermissions` and `declaredAuditEvents`
* entry under the entry's own `appVersion`, dedupes the permission rows, and writes
* `AppDesiredState.fixturePinsByVersion` to `release.app_version_fixtures`.
*
* The reader is release-engine's declaration stamp. It sends the declarations of every app version,
* each tagged, and the fixture pins, only to a data plane whose last status report names this entry.
* An operator program built before #4416 writes every permission row under the app-level version,
* and two versions that declare one key abort its sync transaction. Absence means "writes one
* version only", never "unknown".
*
* The value is the literal `'per-version-declarations'`, inside `CAPABILITY_PATTERN`. Both ends
* import this constant; neither spells the string.
*/
const PER_VERSION_DECLARATIONS_CAPABILITY = "per-version-declarations";
const CapabilitySchema = z.string().regex(CAPABILITY_PATTERN);
/**
* One tenant decision on one share, as the operator reports it up (private-release-channels §2c,
* R-002).
*
* - `shareId`: the CP `release.channel_shares.id` the envelope offered.
* - `orgId`: the CP org id (R-017); the operator translates from its DP id.
* - `state`: `accepted` or `declined`. `pending` never crosses the wire; it is the absence of a
*   decision.
* - `decidedAt`: ISO 8601 UTC, at most 40 characters (a millisecond stamp is 24; the bound refuses
*   an unbounded fraction; a stamp in the future is the CP ingest's fence, R-058).
* - `decidedByUserId`: the deciding DP user's id. The email stays on the data plane.
*
* Strict. A conforming object parses to itself. Pure.
*/
const ChannelShareDecisionSchema = z.object({
	shareId: z.uuid(),
	orgId: z.uuid(),
	state: channelShareDecisionStateSchema.exclude(["pending"]),
	decidedAt: z.iso.datetime().max(40),
	decidedByUserId: z.uuid()
}).strict();
/**
* 2026-10-14-aws-dp-parity D3. The most adoption rows one status report may carry in
* `appAdoptions[]`: 256. The operator pages the rest across reports, as it pages
* `channelShareDecisions`. An over-cap array is refused, not trimmed, and `StatusReportSchema` is
* strict end to end, so the refusal fails the whole report: the producer's paging is mandatory.
*
* Size: one row is 153 bytes in compact JSON with a millisecond `updatedAt` and the state
* `accepted`, and 169 bytes at every field's longest form (a 40-character `updatedAt`), so a full
* page with its commas and brackets is 39,425 to 43,521 bytes of the 393,216-byte report budget
* (`STATUS_REPORT_BUDGET_BYTES`). dp-app held
* 46 rows on 2026-10-14, one page. Exported so the producer's page size and this `.max()` are the
* same literal.
*
* The cap is also the pass signal (finding WS-1-R4). The producer fills every page to the cap while
* it holds unsent rows. So a page of fewer rows closes the producer's pass, and only that page lets
* the CP filter the desired state. `StatusReportSchema.appAdoptions` holds the rules.
*/
const APP_ADOPTIONS_PER_REPORT_MAX = 256;
/**
* One org's adoption state of one app, as the operator reports it up (2026-10-14-aws-dp-parity D3).
* The source is the DP row `release.org_app_entitlements`, which core-api serves to the operator on
* `GET /internal/v1/app-adoptions`.
*
* - `orgId`: the CP org id (`platform.organizations.id`), as in `ChannelShareDecisionSchema`; the
*   operator translates from the DP id through the roster on slug.
* - `appId`: the CP `release.apps.id`, a UUID, the value `AppOffer.appId` carries down.
* - `state`: one of the four adoption states of `@zion/sdk-contracts` (`appAdoptionStateSchema`):
*   `offered`, `accepted`, `paused`, `removed`. Only `accepted` is live.
* - `updatedAt`: the row's last change, ISO 8601 UTC, at most 40 characters. The CP keeps the row
*   with the later `updatedAt`.
*
* Trust: a data plane writes this row, and this schema checks the shape alone. Release-engine fences
* each row at ingest, as it fences `channelShareDecisions` (R-006 of the plan):
*
* 1. The org must be an active org placed on the reporting data plane.
* 2. The app must not be a first-party app (`FIRST_PARTY_APPS`). The data plane seeds those
*    `accepted` for every org, and the CP never offers them.
* 3. The app must be an offerable app: a `release.apps` row of kind `app`. A library, an instance
*    and an unknown id drop. This check reads the registry alone. It reads no channel pin and no
*    channel share, so it does not ask whether the CP offers the app to that org. The fence keeps a
*    row in any of the four states for an offerable app, also when the app has no public pin and
*    no channel share to that org, and also while the CP offers the app to nobody now. The data
*    plane sends each row once, so a dropped row is lost.
* 4. A stored row admits nothing alone. The desired-state filter is the gate. It admits an app
*    only when the CP offers it to the org now (a public pin, or a channel share to that org) and
*    the org's row is `accepted` (D1). So a data plane cannot make an app run that its author did
*    not approve. A stored `accepted` row for an app that the CP offers to the org later admits the
*    app when that offer starts, with no new accept on the data plane.
*
* Strict. A conforming object parses to itself. No `.transform()`. Pure.
*/
const AppAdoptionReportSchema = z.object({
	orgId: z.uuid(),
	appId: z.uuid(),
	state: appAdoptionStateSchema,
	updatedAt: z.iso.datetime().max(40)
}).strict();
/**
* 2026-10-13-blob-buckets R-071 item 1. The most settings rows one status report may carry in
* `blobBucketSettings[]`: 256. The operator program pages the rest across reports, as it pages
* `appAdoptions`. An over-cap array is refused, not trimmed, and `StatusReportSchema` is strict end
* to end, so the refusal fails the whole report: the producer's paging is mandatory.
*
* Size: one row is 224 bytes of compact JSON in the common form (`uploads`, `us-central1`, no
* `deleteAfterDays`, a millisecond `updatedAt`), and 318 bytes at every field's longest form (a
* 63-character `logical`, a 31-character `location`, both day counts at 3650 and a 40-character
* `updatedAt`). A full page with its commas and brackets is 57,601 to 81,665 bytes. With a full
* `appAdoptions` page (at most 43,521 bytes) the two event members hold at most 125,186 bytes, 31.8
* percent of the 393,216-byte report budget (`STATUS_REPORT_BUDGET_BYTES`). Exported so the
* producer's page size and this `.max()` are the same literal.
*/
const BLOB_BUCKET_SETTINGS_PER_REPORT_MAX = 256;
/**
* The key of one blob bucket settings row: `<orgId>/<appId>/<logical>`. One `(org, app, logical
* bucket)` triple has at most one row in a status report page and in the core-api sync answer.
*
* Who calls it: the duplicate checks of {@link StatusReportSchema} `blobBucketSettings` and of
* `BlobBucketSettingsSyncResponseSchema`; the operator program's page ledger (WS-46); and
* release-engine's upsert (WS-45), which keys `release.dp_org_blob_bucket_settings` on the same
* triple plus the data plane.
*
* Input: any object with the three string members. A `/` cannot occur inside a UUID or a logical
* bucket name (`BLOB_BUCKET_NAME_PATTERN`), so two distinct valid triples never give one key.
* Output: the three values joined by `/`. Errors: none. Pure.
*/
function blobBucketSettingsKey(row) {
	return `${row.orgId}/${row.appId}/${row.logical}`;
}
/**
* Add one issue at `[index, 'logical']` for each row whose {@link blobBucketSettingsKey} repeats an
* earlier row. The first row of a key stays clean. Shared by the two row arrays of this contract.
*/
function refineDistinctBlobBucketSettings(rows, ctx) {
	const seen = /* @__PURE__ */ new Set();
	rows.forEach((row, i) => {
		const key = blobBucketSettingsKey(row);
		if (seen.has(key)) ctx.addIssue({
			code: "custom",
			path: [i, "logical"],
			message: "duplicate (orgId, appId, logical) triple"
		});
		seen.add(key);
	});
}
/**
* One org's settings of one logical bucket of one app (2026-10-13-blob-buckets R-069 item 1,
* R-071 items 1 and 2). The source is the DP row `release.org_blob_bucket_settings`, which core-api
* serves to the operator program on `GET /internal/v1/blob-bucket-settings` (WS-47). core-api
* answers one row for every accepted adoption and declared allocatable bucket, with a NULL
* `location` when the org picked none (R-071 item 3).
*
* - `orgId`: the CP org id (`platform.organizations.id`) in a status report; the operator program
*   translates from the DP org id through the roster, as for `appAdoptions`. The same row shape
*   with the DP org id is the row of the core-api sync answer (`BlobBucketSettingsSyncResponseSchema`).
* - `appId`: the CP `release.apps.id`, a UUID, the value `AppOffer.appId` carries down.
* - `logical`: the logical bucket name the app declares, in `blobBucketNameSchema` of
*   `@zion/sdk-contracts` (3 to 63 characters of `BLOB_BUCKET_NAME_PATTERN`).
* - `location`: the location the org admin picked at adoption or in the bucket settings, in
*   `dataLocationSchema` (a single region; `us` and `eu` are refused). `null` means "no pick": the
*   CP gives the bucket the org data location (R-069 item 1).
* - `deleteAfterDays`: the org's bucket-wide delete age, an integer from 1 to
*   `BLOB_BUCKET_DELETE_AFTER_DAYS_MAX` (3650), or `null` for "no org override". core-api refuses a
*   value shorter than the app's (R-071 item 6); the CP applies the longer of the two (WS-45).
* - `removalGraceDays`: the days after the org removes the app before the platform deletes the
*   bucket, an integer from 1 to 3650. core-api stores 30 when the org set none. The CP applies the
*   longer of the app and the org value (WS-45).
* - `updatedAt`: the row's last change, ISO 8601 UTC, at most 40 characters. The CP keeps the row
*   with the later `updatedAt` (WS-45).
*
* The absence of a row is the signal (R-071 item 8). The CP creates no bucket for a new
* `(org, app, logical)` triple until its row arrives (outcome `waiting-settings`). A row with a
* `location` replaces the resolved org data location; a row with `null` keeps it.
*
* Trust: a data plane writes this row, and this schema checks the shape alone. Release-engine fences
* each row at ingest as it fences `appAdoptions`: the org must be placed on the reporting data plane
* and the app must be an offerable app. A `logical` that the app does not declare is stored and
* never allocated, because the allocator reads the declared buckets.
*
* Strict. A conforming object parses to itself. No `.transform()`. Pure.
*/
const BlobBucketSettingsReportSchema = z.object({
	orgId: z.uuid(),
	appId: z.uuid(),
	logical: blobBucketNameSchema,
	location: dataLocationSchema.nullable(),
	deleteAfterDays: z.number().int().min(1).max(BLOB_BUCKET_DELETE_AFTER_DAYS_MAX).nullable(),
	removalGraceDays: z.number().int().min(1).max(BLOB_BUCKET_DELETE_AFTER_DAYS_MAX),
	updatedAt: z.iso.datetime().max(40)
}).strict();
/**
* 2026-10-13-blob-buckets R-089 item 2. The most org admin relocation requests one status report
* may carry in `blobRelocationRequests[]`: 64. The operator program pages the rest across reports,
* as it pages `blobBucketSettings`. An over-cap array is refused, not trimmed, so the paging of the
* producer is mandatory.
*
* Size: one row is 214 bytes of compact JSON in the common form (`sdk-smoke`, `uploads`,
* `europe-west1`, a millisecond `requestedAt`), and 360 bytes at every field's longest form (a
* 64-character `appSlug`, a 63-character `logical`, a 31-character `targetLocation` and a
* 40-character `requestedAt`). A full page with its commas and brackets is at most 23,105 bytes,
* 5.9 percent of the 393,216-byte report budget
* (`STATUS_REPORT_BUDGET_BYTES`). Exported so the page size of the producer and this `.max()` are
* one literal.
*/
const BLOB_RELOCATION_REQUESTS_PER_REPORT_MAX = 64;
/**
* Add one issue at `[index, 'requestId']` for each row whose `requestId` repeats an earlier row. The
* first row of an id stays clean. Shared by the status report member and the core-api page of
* `BlobRelocationRequestsSyncResponseSchema`.
*/
function refineDistinctBlobRelocationRequests(rows, ctx) {
	const seen = /* @__PURE__ */ new Set();
	rows.forEach((row, i) => {
		if (seen.has(row.requestId)) ctx.addIssue({
			code: "custom",
			path: [i, "requestId"],
			message: "duplicate requestId"
		});
		seen.add(row.requestId);
	});
}
/**
* One org admin request to move the buckets of one app to a new location (2026-10-13-blob-buckets
* R-089 item 2, request shape c1 of `research/s4b-request-carrier.md`). The source is the DP row
* `release.org_blob_relocation_requests`, which core-api writes on
* `POST /v1/admin/blob-buckets/{appSlug}/{logical}/relocations` and serves to the operator program
* on `GET /internal/v1/blob-relocation-requests` (WS-51).
*
* - `requestId`: the id core-api minted for the request, a lower-case UUID. It is the idempotency
*   key of the request on every hop: release-engine stores one row per `(dp_id, request_id)`
*   (`release.blob_relocation_requests`, WS-21), and provisioning-svc handles each id once
*   (`provisioning.relocation_requests`, WS-52).
* - `orgId`: the CP org id (`platform.organizations.id`, R-017) in a status report, a lower-case
*   UUID; the operator program translates from the DP org id through the roster, as for
*   `blobBucketSettings`. The same row shape with the DP org id is the row of the core-api page.
* - `appSlug`: the app slug, in the grammar of `BlobBindingsFile.app` (2 to 64 characters of
*   `RESOURCE_SLUG_PATTERN`).
* - `logical`: the logical bucket name the org admin asked about, in `blobBucketNameSchema` of
*   `@zion/sdk-contracts`. The plan moves every app bucket of the org and the app (R-079), so this
*   name tells where the request came from and limits nothing.
* - `targetLocation`: the location to move to, in `dataLocationSchema` (a single region). core-api
*   checked it against the allowed list of the data plane; provisioning-svc checks it again against
*   its own list (R-065).
* - `requestedAt`: the instant core-api stored the request, ISO 8601 UTC, at most 40 characters.
*
* Trust: a data plane writes this row, and this schema checks the shape alone. Release-engine fences
* each row at ingest with the checks of `fenceAppAdoptions`: the org must be placed on the
* reporting data plane and the app must be an offerable app. A row the fence keeps is stored with a
* conflict skip on `(dp_id, request_id)`, so a repeat row changes nothing.
*
* Strict. A conforming object parses to itself. No `.transform()`. Pure.
*/
const BlobRelocationRequestReportSchema = z.object({
	requestId: z.string().regex(LOWERCASED_UUID_PATTERN, "a lower-case UUID"),
	orgId: z.string().regex(LOWERCASED_UUID_PATTERN, "a lower-case UUID"),
	appSlug: blobBindingsFileSchema.shape.app,
	logical: blobBucketNameSchema,
	targetLocation: dataLocationSchema,
	requestedAt: z.iso.datetime().max(40)
}).strict();
/**
* How old the latest satellite status message may be before its satellite reads `stale`: 120
* seconds, four of the operator's 30-second `pollIntervalSeconds` cycles, each of which publishes
* one message (R-012). Exported so the home operator's classifier and any test of it read one figure.
*/
const SATELLITE_STATUS_STALE_AFTER_MS = 12e4;
/**
* Most census rows one satellite may report. A satellite runs one system node pool and its
* regional app-pool node groups, at most `APP_POOL_NODE_GROUP_MAX` (100). A census row is one
* `(role, nodeType)` pair, or one group of that pair when the row carries `groupId` (D-35). So the
* app-pool rows are at most one per group, 100, and the system pool adds at most one row per other
* role (six): 106 at worst, under this cap. Over-cap is refused, not trimmed, and the producer
* slices.
*/
const SATELLITE_CENSUS_ROWS_MAX = 128;
/**
* Most satellites one status report may carry. One satellite per region per data plane (the
* table's primary key), and GCP has fewer than 64 regions, so no admissible configuration reaches
* the cap. Over-cap is refused, not trimmed, which fails the whole `.strict()` report, so the
* producer slices to this figure.
*/
const SATELLITE_STATUS_MAX = 64;
/**
* One regional node group as the satellite observes it: the nodes carrying the group's
* `zion.witness.ai/app-pool-group` label in the satellite cluster.
*
* - `groupId`: the group's `apg-` id, as the home compiler derived it with the region.
* - `nodeCount`: the nodes in the group, a whole number from 0 to `APP_POOL_NODES_MAX` (200) in
*   `@zion/sdk-contracts` (R-030). The home compiler caps a group's `maxNodeCount` at 100, and the
*   old and the new node sets of one group overlap while GKE rolls it, so a real group can hold
*   twice that for a while. A measured `0` is an answer.
* - `readyNodeCount`: the subset whose `Ready` condition is `True`, 0 to `APP_POOL_NODES_MAX` and
*   never above `nodeCount`.
*
* The satellite has no desired state, so it cannot report a group's floor, ceiling or pools; those
* ride the home compiler's `StatusReport.appPoolNodeGroups` items with `region` (WS-9, R-011). `.strict()`.
* A conforming object parses to itself; anything else is a `ZodError`. Pure.
*/
const SatelliteNodeGroupObservationSchema = z.object({
	groupId: z.string().regex(APP_POOL_NODE_GROUP_ID_PATTERN$1),
	nodeCount: z.number().int().min(0).max(APP_POOL_NODES_MAX),
	readyNodeCount: z.number().int().min(0).max(APP_POOL_NODES_MAX)
}).strict().refine((g) => g.readyNodeCount <= g.nodeCount, {
	message: "readyNodeCount cannot exceed nodeCount",
	path: ["readyNodeCount"]
});
/**
* Most standing member sets one satellite may hold, and so most `members[]` rows one status message
* may carry and most membership entries one desired-state slice may carry: 2,000
* (2026-10-02-regional-pools-release-2 WS-2).
*
* The figure is `APP_POOL_NODE_GROUP_MAX * APP_POOL_MAX_POOLS_PER_APP`, 100 groups of 20 pools. A
* member set is one `(pool, org)` pair of an out-of-mesh standing pool, and it runs on that org's
* node group. A group hashes one app and one org, so the pools that share it are pools of one app,
* at most `APP_POOL_MAX_POOLS_PER_APP`. A data plane compiles at most `APP_POOL_NODE_GROUP_MAX`
* groups. Over-cap is refused, not trimmed, and the producer slices. Exported so the producer's
* slice and the two `.max()` bounds read one figure.
*/
const APP_POOL_SATELLITE_MEMBERS_MAX = 100 * APP_POOL_MAX_POOLS_PER_APP;
/**
* One standing member set as the satellite observes it (2026-10-02-regional-pools-release-2 §2.1):
* the member pods of one pool on one regional node group.
*
* - `groupId`: the node group the members run on, in the `apg-` grammar. The home operator maps it
*   to the app and the org through its own compiler output, because the group hashes both.
* - `pool`: the pool's name, in `appPoolNameSchema`'s grammar. Several pools of one app can share a
*   group, so the pair `(groupId, pool)` names one member set, and the pair appears once per message.
* - `total`: the member pods that exist for the set, a whole number from 0 to
*   `APP_POOL_CONCURRENCY_MAX` (1,000), the most members any pool declaration asks for. A measured
*   `0` is an answer: the set is desired and no pod exists yet.
* - `ready`: the subset whose pod is `Running` with an address and `Ready`, 0 to `total`.
*
* `.strict()`. A conforming object parses to itself; anything else is a `ZodError`. Pure.
*/
const SatelliteMemberObservationSchema = z.object({
	groupId: z.string().regex(APP_POOL_NODE_GROUP_ID_PATTERN$1),
	pool: appPoolNameSchema,
	ready: z.int().min(0).max(APP_POOL_CONCURRENCY_MAX),
	total: z.int().min(0).max(APP_POOL_CONCURRENCY_MAX)
}).strict().refine((m) => m.ready <= m.total, {
	message: "ready cannot exceed total",
	path: ["ready"]
});
/** Whether no `(groupId, pool)` pair appears twice among the member rows. */
function distinctMemberSets(members) {
	return members === void 0 || new Set(members.map((m) => JSON.stringify([m.groupId, m.pool]))).size === members.length;
}
/**
* Most nodes one satellite census row may count, in `nodeCount` and in `readyNodeCount`:
* `APP_POOL_NODE_GROUP_MAX * APP_POOL_NODES_MAX`, 100 groups of at most 200 nodes each, 20,000
* (R-030). A census row with no `groupId` sums every node of one `(role, nodeType)` in the
* cluster, and several regional groups can share one machine type, so the row's ceiling is what
* the whole cluster can hold. A per-group figure would refuse a real row: two apps' groups of 60
* `c4d-standard-8` nodes make a row of 120. A row with a `groupId` takes the group bound of
* {@link appPoolCensusWithinEachGroup} as well.
*/
const SATELLITE_CENSUS_NODES_MAX = 100 * APP_POOL_NODES_MAX;
/**
* One census row as a satellite reports it: a `LiveNodePool` row whose `nodeCount` and
* `readyNodeCount` are at most 20,000, `APP_POOL_NODE_GROUP_MAX * APP_POOL_NODES_MAX` (R-030).
*
* The satellite is semi-trusted, and the home operator copies a `ready` satellite's `app-pool` rows
* into the top-level `livePools`, which metering sums (R-005). Without a bound one row could claim
* any node count for its region. A row over the bound is a `ZodError` naming the count, the whole
* message or entry is refused, and the home operator keeps the satellite's previous phase and
* figures. The row bound does not bound the message: 128 rows of 20,000 would each pass it. The
* message and entry schemas carry that bound, the `app-pool` rows' `nodeCount` sum at most the
* `nodeGroups` sum. The home `livePools` rows keep `liveNodePoolSchema`'s own bounds. Pure.
*/
const satelliteCensusRowSchema = liveNodePoolSchema.refine((row) => row.nodeCount <= SATELLITE_CENSUS_NODES_MAX, {
	message: `nodeCount cannot exceed ${SATELLITE_CENSUS_NODES_MAX}`,
	path: ["nodeCount"]
}).refine((row) => row.readyNodeCount <= SATELLITE_CENSUS_NODES_MAX, {
	message: `readyNodeCount cannot exceed ${SATELLITE_CENSUS_NODES_MAX}`,
	path: ["readyNodeCount"]
});
/** Whether every census row names the region the message or entry is for. */
function censusInRegion(census, region) {
	return census === void 0 || census.every((row) => row.region === region);
}
/** Whether the measured members agree with the phase: both absent for `pending`, both present for
*  `ready` and `stale`. One present without the other matches neither arm. */
function measuredMatchesPhase(phase, nodeGroups, census) {
	if (phase === "pending") return nodeGroups === void 0 && census === void 0;
	return nodeGroups !== void 0 && census !== void 0;
}
/**
* Whether no `(role, nodeType, groupId, capacityType)` key appears twice among the census rows. An
* absent `groupId` or `capacityType` is a key part of its own, so two rows of one `(role, nodeType)`
* with neither member repeat a key. The home operator copies a `ready` satellite's `app-pool` rows
* into `livePools`, which metering sums. A repeated key would bill one region's nodes twice (round
* 1 security-2).
*
* The key holds `groupId` and `capacityType` because the operator census keys an `app-pool` row by
* its group and its capacity (`buildNodeCensus`, 2026-10-24-pool-machine-generations D-35, WS-3c),
* and the satellite runs the same census code. Two regional groups of one machine type, such as the
* pools of two apps, are then two rows. So is one group whose nodes carry a spot label and an
* on-demand label. A key without either member refuses that message, and the home operator then
* keeps the satellite's previous phase and figures. {@link appPoolCensusWithinEachGroup} sums the
* rows of one group and bounds the sum by that group.
*/
function distinctCensusRows(census) {
	return census === void 0 || new Set(census.map((row) => JSON.stringify([
		row.role,
		row.nodeType,
		row.groupId ?? null,
		row.capacityType ?? null
	]))).size === census.length;
}
/** The refusal message when two census rows share one `(role, nodeType, groupId, capacityType)` key. */
const CENSUS_ROW_REPEAT_MESSAGE = "each (role, nodeType, groupId, capacityType) census row appears once";
/**
* Whether each group's `app-pool` census rows count no more nodes than that group's observation in
* `nodeGroups` (2026-10-24-pool-machine-generations D-35), on the count `count`. The rows of one
* `groupId` sum, and a `groupId` that no observation names holds 0 nodes. So a row cannot bill one
* group's nodes to another group, and a row of a group that the message does not observe can count
* only 0. The schemas run this check twice: on `nodeCount`, and on `readyNodeCount`, so a census
* row cannot claim a ready node that its group reports as not ready.
*
* A truthful satellite derives both members from one node list read. A group observation counts
* every node with the group's label, and its ready subset. The census puts such a node in the
* `app-pool` row of its group unless a more specific role claims it first, and it counts the node
* as ready on the same `Ready` condition. So the census side can fall short of the group count on
* each count, and never exceed it. Rows with no `groupId` are outside this check;
* {@link appPoolCensusWithinGroups} bounds them with the whole-cluster sum. `true` when either
* member is absent, which the phase refine judges.
*/
function appPoolCensusWithinEachGroup(census, nodeGroups, count) {
	if (census === void 0 || nodeGroups === void 0) return true;
	const claimed = /* @__PURE__ */ new Map();
	for (const row of census) {
		if (row.role !== "app-pool" || row.groupId === void 0) continue;
		claimed.set(row.groupId, (claimed.get(row.groupId) ?? 0) + row[count]);
	}
	const observed = new Map(nodeGroups.map((group) => [group.groupId, group[count]]));
	return [...claimed].every(([groupId, nodes]) => nodes <= (observed.get(groupId) ?? 0));
}
/** The refusal message when a group's `app-pool` census rows out-count that group's observation. */
const APP_POOL_CENSUS_OVER_GROUP_MESSAGE = "the app-pool census rows of a group cannot count more nodes than that node group";
/** The refusal message when a group's `app-pool` census rows count more ready nodes than that
*  group's observation reports ready. */
const APP_POOL_CENSUS_READY_OVER_GROUP_MESSAGE = "the app-pool census rows of a group cannot count more ready nodes than that node group";
/**
* Whether the `app-pool` census rows carry a `groupId` on every row or on none
* (2026-10-24-pool-machine-generations D-35).
*
* The census gives a node the `app-pool` role only when the node carries a non-empty
* `zion.witness.ai/app-pool-group` label (`node-census.ts` in the operator). An operator with the
* group key of WS-3c writes that label value as the `groupId` of each `app-pool` row. An operator
* before WS-3c writes `groupId` on no row. So one census from one operator never mixes the two
* forms. A mix lets an ungrouped row count nodes that a grouped row of the same machine type also
* counts, and the home operator copies both rows into `livePools`, which metering sums. `true` when
* the census is absent, which the phase refine judges.
*/
function appPoolCensusGroupedAllOrNone(census) {
	if (census === void 0) return true;
	const appPool = census.filter((row) => row.role === "app-pool");
	return appPool.every((row) => row.groupId !== void 0) || appPool.every((row) => row.groupId === void 0);
}
/** The refusal message when the `app-pool` census rows mix rows with and without a `groupId`. */
const APP_POOL_CENSUS_MIXED_GROUPING_MESSAGE = "the app-pool census rows carry a groupId on every row or on none";
/**
* Whether the `app-pool` census rows count no more nodes than the node groups do (R-030, round 5
* architecture-10). Both figures count the satellite's nodes carrying `zion.witness.ai/app-pool-group`:
* each group observation counts its own label value, and the census classifier puts a labelled node
* in the `app-pool` role unless a more specific role (`dedicated`, `infra`, `default-app`) claims it
* first. So a truthful satellite that derives both from one node list gives an `app-pool` sum at most
* the group sum, and the group sum is at most `APP_POOL_NODE_GROUP_MAX * APP_POOL_NODES_MAX`
* (20,000). The per-row bound alone lets 128 rows claim 20,000 each. The other roles are the
* satellite's system pool and stay under the per-row bound only. The schemas run this check on
* `nodeCount` and on `readyNodeCount` (the argument `count`). A row with no `groupId` is outside
* {@link appPoolCensusWithinEachGroup}, so the `readyNodeCount` run is what stops such a row from
* reporting more ready nodes than the groups report ready. `true` when either member is absent,
* which the phase refine judges.
*/
function appPoolCensusWithinGroups(census, nodeGroups, count) {
	if (census === void 0 || nodeGroups === void 0) return true;
	return census.reduce((sum, row) => row.role === "app-pool" ? sum + row[count] : sum, 0) <= nodeGroups.reduce((sum, group) => sum + group[count], 0);
}
/** The refusal message when the `app-pool` census rows out-count the node groups. */
const APP_POOL_CENSUS_OVER_GROUPS_MESSAGE = "the app-pool census rows cannot count more nodes than the node groups";
/** The refusal message when the `app-pool` census rows count more ready nodes than the node groups
*  report ready. */
const APP_POOL_CENSUS_READY_OVER_GROUPS_MESSAGE = "the app-pool census rows cannot count more ready nodes than the node groups";
/** Whether no group id appears twice. */
function distinctGroupIds(groups) {
	return groups === void 0 || new Set(groups.map((g) => g.groupId)).size === groups.length;
}
/**
* The status message one satellite operator publishes on `appPoolSatelliteStatusSubject` once per
* reconcile cycle (D-19, R-005).
*
* - `region`: the satellite's own region. The home operator discards a message whose `region`
*   differs from the subject's region token or names no entry of `desired.satellites`.
* - `nodeGroups`: every regional node group with at least one node in the satellite cluster, at
*   most `APP_POOL_NODE_GROUP_MAX`, each group id once. See {@link SatelliteNodeGroupObservationSchema}.
* - `census`: the satellite cluster's live node census, `LiveNodePool` rows computed by the same
*   `node-census` code the home operator runs, at most {@link SATELLITE_CENSUS_ROWS_MAX}. Every row
*   carries `region` equal to the message's (refine), so a satellite cannot report nodes under
*   another region, and each `(role, nodeType, groupId, capacityType)` key appears once (refine),
*   so a satellite cannot report one pool's nodes twice. Each row's `nodeCount` and `readyNodeCount` are at most
*   `APP_POOL_NODE_GROUP_MAX * APP_POOL_NODES_MAX`, 20,000, and each group's at most
*   `APP_POOL_NODES_MAX`, 200 (R-030).
* - The `app-pool` census rows' `nodeCount` sum is at most the `nodeGroups` `nodeCount` sum
*   (refine, path `['census']`), so the nodes the home operator copies into `livePools` for this
*   region never exceed what the groups hold, at most 20,000 in the whole message. Both figures
*   count the nodes carrying `zion.witness.ai/app-pool-group`, and the census classifier can give a
*   labelled node a more specific role, so the census side can fall short and never exceed. The
*   satellite operator (WS-10) must derive `nodeGroups` and `census` from one node list read; two
*   reads would let a node that joins between them put the census one node over, and the home
*   operator would drop the message.
* - The `app-pool` census rows' `readyNodeCount` sum is at most the `nodeGroups` `readyNodeCount`
*   sum (refine, path `['census']`), on the same ground: a truthful census counts a labelled node as
*   ready on the `Ready` condition its group observation reads.
* - The `app-pool` census rows of one `groupId` count at most the `nodeCount` of that group's
*   observation, and 0 for a `groupId` that `nodeGroups` does not name (refine, path `['census']`,
*   D-35). So a satellite cannot bill one group's nodes to another group. The same rule holds on
*   `readyNodeCount` (refine, path `['census']`), so a row cannot report a ready node that its group
*   reports as not ready.
* - The `app-pool` census rows carry a `groupId` on every row or on none (refine, path
*   `['census']`, D-35), because one operator writes one form. So an ungrouped row cannot count the
*   nodes of a grouped row a second time.
* - `members`: the standing member sets the satellite runs from the desired-state slice it holds
*   (2026-10-02-regional-pools-release-2 §2.1), one row per `(groupId, pool)` (refine), at most
*   {@link APP_POOL_SATELLITE_MEMBERS_MAX}. See {@link SatelliteMemberObservationSchema}. The home
*   operator merges the rows, so its census and the capacity page count a regional member as a
*   member. The parse does not tie a row to `nodeGroups`; the home operator drops and logs a row
*   whose `groupId` the same message's `nodeGroups` does not name, and merges the rest (R-013).
*   Absent means the satellite holds no slice: a release-1 satellite, or one that has
*   received no slice since it started. `[]` means it holds a slice that asks for no member. The
*   satellite omits the member until it applies its first slice, so a home operator built before
*   this member, which publishes no slice, never receives it and never drops the message for it.
*
* The satellite is semi-trusted: the NATS grant confines its publishes to its own region's
* subtree, and this `.strict()` parse confines the body. A conforming object parses to itself;
* anything else is a `ZodError`, and the home operator drops the message and keeps the entry's
* previous state: a message over any count bound leaves the satellite's phase unchanged (R-027, R-030).
* Pure.
*/
const SatelliteStatusMessageSchema = z.object({
	region: appPoolRegionSchema,
	nodeGroups: z.array(SatelliteNodeGroupObservationSchema).max(100),
	census: z.array(satelliteCensusRowSchema).max(128),
	members: z.array(SatelliteMemberObservationSchema).max(APP_POOL_SATELLITE_MEMBERS_MAX).optional()
}).strict().refine((m) => censusInRegion(m.census, m.region), {
	message: "every census row must carry the satellite region",
	path: ["census"]
}).refine((m) => distinctGroupIds(m.nodeGroups), {
	message: "each groupId appears once",
	path: ["nodeGroups"]
}).refine((m) => distinctCensusRows(m.census), {
	message: CENSUS_ROW_REPEAT_MESSAGE,
	path: ["census"]
}).refine((m) => appPoolCensusWithinGroups(m.census, m.nodeGroups, "nodeCount"), {
	message: APP_POOL_CENSUS_OVER_GROUPS_MESSAGE,
	path: ["census"]
}).refine((m) => appPoolCensusWithinGroups(m.census, m.nodeGroups, "readyNodeCount"), {
	message: APP_POOL_CENSUS_READY_OVER_GROUPS_MESSAGE,
	path: ["census"]
}).refine((m) => appPoolCensusWithinEachGroup(m.census, m.nodeGroups, "nodeCount"), {
	message: APP_POOL_CENSUS_OVER_GROUP_MESSAGE,
	path: ["census"]
}).refine((m) => appPoolCensusWithinEachGroup(m.census, m.nodeGroups, "readyNodeCount"), {
	message: APP_POOL_CENSUS_READY_OVER_GROUP_MESSAGE,
	path: ["census"]
}).refine((m) => appPoolCensusGroupedAllOrNone(m.census), {
	message: APP_POOL_CENSUS_MIXED_GROUPING_MESSAGE,
	path: ["census"]
}).refine((m) => distinctMemberSets(m.members), {
	message: "each (groupId, pool) member row appears once",
	path: ["members"]
});
/**
* One satellite as the home operator reports it on `StatusReport.satellites` (D-19, R-005): the
* latest status message's `region`, `nodeGroups` and `census`, plus the `phase` the home operator
* assigns from its receive clock (see {@link SATELLITE_PHASES}).
*
* `nodeGroups` and `census` are absent exactly when `phase` is `pending` and present otherwise
* (refine): absent means nothing was measured, and a zero would claim a measurement. The same
* region, cap, uniqueness, group-sum, per-group and grouping rules as
* {@link SatelliteStatusMessageSchema} hold, on `nodeCount` and on `readyNodeCount`.
*
* This entry is display state for the console's Regions section. Billing never reads it: the home
* operator merges a `ready` satellite's `app-pool` census rows into the top-level `livePools` with
* `region` set, and metering reads `live_pools` (R-005). `.strict()`. A conforming object parses to
* itself; anything else is a `ZodError`, and because `StatusReportSchema` is strict end to end that
* fails the whole report. Pure.
*/
const SatelliteStatusSchema = z.object({
	region: appPoolRegionSchema,
	phase: z.enum(SATELLITE_PHASES),
	nodeGroups: z.array(SatelliteNodeGroupObservationSchema).max(100).optional(),
	census: z.array(satelliteCensusRowSchema).max(128).optional()
}).strict().refine((e) => measuredMatchesPhase(e.phase, e.nodeGroups, e.census), {
	message: "nodeGroups and census are absent for a pending satellite and present otherwise",
	path: ["phase"]
}).refine((e) => censusInRegion(e.census, e.region), {
	message: "every census row must carry the satellite region",
	path: ["census"]
}).refine((e) => distinctGroupIds(e.nodeGroups), {
	message: "each groupId appears once",
	path: ["nodeGroups"]
}).refine((e) => distinctCensusRows(e.census), {
	message: CENSUS_ROW_REPEAT_MESSAGE,
	path: ["census"]
}).refine((e) => appPoolCensusWithinGroups(e.census, e.nodeGroups, "nodeCount"), {
	message: APP_POOL_CENSUS_OVER_GROUPS_MESSAGE,
	path: ["census"]
}).refine((e) => appPoolCensusWithinGroups(e.census, e.nodeGroups, "readyNodeCount"), {
	message: APP_POOL_CENSUS_READY_OVER_GROUPS_MESSAGE,
	path: ["census"]
}).refine((e) => appPoolCensusWithinEachGroup(e.census, e.nodeGroups, "nodeCount"), {
	message: APP_POOL_CENSUS_OVER_GROUP_MESSAGE,
	path: ["census"]
}).refine((e) => appPoolCensusWithinEachGroup(e.census, e.nodeGroups, "readyNodeCount"), {
	message: APP_POOL_CENSUS_READY_OVER_GROUP_MESSAGE,
	path: ["census"]
}).refine((e) => appPoolCensusGroupedAllOrNone(e.census), {
	message: APP_POOL_CENSUS_MIXED_GROUPING_MESSAGE,
	path: ["census"]
});
/**
* The header members of a status report (WS-1). A delta carries each one exactly as a full report
* does: the required ones always, the optional ones when the operator knows them. No hash covers
* them, so a change in a header member never makes a section body travel. `reportedAt` is here,
* so the clock never moves a hash (D-16). `capabilities` is here, so a delta can never flip the
* capability gates of the desired state off for one poll. `doorPull` is here
* (2026-10-15-per-dp-door-identity D8), so every report carries the refresher's state: the CP's
* bootstrap mint keys on it, and a delta that dropped it would restart the mint for one poll.
*/
const STATUS_REPORT_HEADER_MEMBERS = [
	"dataPlaneId",
	"channelName",
	"platformVersion",
	"helmChartVersion",
	"health",
	"operatorVersion",
	"reportedAt",
	"orgCount",
	"capabilities",
	"doorPull"
];
/**
* The event members of a status report (D-16). Each is a list of records that the CP stores with
* an idempotent write and never replaces: `podLifecycle` rows insert with a conflict skip, and
* `channelShareDecisions`, `appAdoptions` and `blobBucketSettings` rows upsert. The operator sends
* each one whenever it holds records, in a full report and in a delta alike. No hash covers them.
* The operator also sends `appAdoptions` as `[]` after each read of core-api, because a short page
* closes its first pass (finding WS-1-R4); a delta keeps that `[]`. `blobBucketSettings` has no
* pass rule (2026-10-13-blob-buckets R-071 item 8): an absent row means "not yet sent".
* `blobRelocationRequests` rows insert with a conflict skip on the request id, and it has no pass
* rule either (2026-10-13-blob-buckets R-089 item 2).
*/
const STATUS_REPORT_EVENT_MEMBERS = [
	"podLifecycle",
	"channelShareDecisions",
	"appAdoptions",
	"blobBucketSettings",
	"blobRelocationRequests"
];
/**
* The sections of a status report (D-6). Each section that a report carries has one etag in
* `sectionEtags`, under its own name. A section that the report omits has no key there, and the CP
* reads that absence as it reads an absent member of a full report today.
*
* The order is the order of the members in `StatusReportSchema`. It decides nothing: the etag map
* is hashed in canonical JSON, with sorted keys.
*/
const STATUS_REPORT_SECTIONS = [
	"services",
	"lastMigration",
	"observability",
	"envelopeErrors",
	"customerChannels",
	"computeUnitInUseNodeTypes",
	"computeResolutions",
	"computeResolutionsTruncated",
	"livePools",
	"unsatisfiedDemand",
	"appPoolNodeGroups",
	"appPools",
	"satellites"
];
/**
* The sections that every full report carries, so every `sectionEtags` map names them:
* `services`, `lastMigration` and `observability`. They are the required members of
* `StatusReportSchema` that are not header members and not `apps`.
*/
const STATUS_REPORT_REQUIRED_SECTIONS = [
	"services",
	"lastMigration",
	"observability"
];
/**
* The members that the operator may trim from an over-budget full report, in the order it trims
* them (D-10). The CP tolerates each one as missing for one cycle (`research/report-consumers.md`).
*
* `apps`, `computeUnitInUseNodeTypes` and `capabilities` are never here: an
* absent one makes the CP take a wrong decision (D-3). `observability` is not here either, because
* `StatusReportSchema` requires it; see the WS-1 escalation in `reports/S1-WS-1.md`.
*
* When `computeResolutions` is trimmed, the operator also sets `computeResolutionsTruncated: true`,
* because the trim dropped rows (D37 of compute-classes).
*/
const STATUS_REPORT_TRIMMABLE_MEMBERS = [
	"podLifecycle",
	"computeResolutions",
	"livePools",
	"unsatisfiedDemand",
	"satellites"
];
/**
* The one key grammar of an app in a status report: any string. The `apps` record of
* `StatusReportSchema` and the `apps` map of `StatusReportSectionEtagsSchema` both read it, so the
* two records always admit the same key set. A narrower grammar on the etag map alone would refuse
* a full report whose `apps` the old schema admits, as soon as the operator adds `sectionEtags`.
* `StatusReportSchema` is strict end to end, so the CP would refuse that report on every cycle and
* go blind (the 2026-10-08 incident class; finding WS-1-R1). Module-private: no consumer reads it.
*/
const STATUS_REPORT_APP_KEY_SCHEMA = z.string();
/** The grammar of one etag: the 64 lowercase hex characters of a SHA-256 digest. */
const STATUS_REPORT_ETAG_PATTERN = /^[0-9a-f]{64}$/;
/**
* One status report etag (D-6): the SHA-256 digest, in 64 lowercase hex characters, that
* `hashStatusReportValue` in `status-report-hash.ts` makes. The brand keeps a plain string out of
* an etag slot at compile time, so only the hash helper or a parse makes one.
*
* Any other string is a `ZodError`: uppercase hex, 63 or 65 characters, or a non-hex character. An
* etag is a content hash of data that the operator sends itself. It is no secret and no MAC, so a
* plain comparison of two etags leaks nothing.
*/
const StatusReportEtagSchema = z.string().regex(STATUS_REPORT_ETAG_PATTERN).brand();
/**
* The etag map of one status report (D-6): one etag per section that the report carries, and one
* etag per app under `apps`. `statusReportSectionEtags` in `status-report-hash.ts` computes it
* from a full report, and the CP stores it in `release.dp_live_status.report_hashes` (D-15).
*
* - `services`, `lastMigration` and `observability` are required, because every full report
*   carries them.
* - Each other section is optional. An absent key means the report omits that section.
* - `apps` maps each app key of the report to the etag of that app. An app absent from it is
*   absent from the report, so a delta removes it.
*
* Strict. Any other key or a malformed etag is a `ZodError`. An app key has the grammar of a key
* of the report's `apps` record ({@link STATUS_REPORT_APP_KEY_SCHEMA}), so every full report that
* parses without the map also parses with the map that `statusReportSectionEtags` computes for it.
* A conforming object parses to itself, except that an own `__proto__` app key leaves the map, as
* it leaves the report's `apps` record. Pure.
*/
const StatusReportSectionEtagsSchema = z.object({
	services: StatusReportEtagSchema,
	lastMigration: StatusReportEtagSchema,
	observability: StatusReportEtagSchema,
	envelopeErrors: StatusReportEtagSchema.optional(),
	customerChannels: StatusReportEtagSchema.optional(),
	computeUnitInUseNodeTypes: StatusReportEtagSchema.optional(),
	computeResolutions: StatusReportEtagSchema.optional(),
	computeResolutionsTruncated: StatusReportEtagSchema.optional(),
	livePools: StatusReportEtagSchema.optional(),
	unsatisfiedDemand: StatusReportEtagSchema.optional(),
	appPoolNodeGroups: StatusReportEtagSchema.optional(),
	appPools: StatusReportEtagSchema.optional(),
	satellites: StatusReportEtagSchema.optional(),
	apps: z.record(STATUS_REPORT_APP_KEY_SCHEMA, StatusReportEtagSchema)
}).strict();
/**
* The `truncated` member of a full report (D-10): the members that the operator trimmed to fit
* `STATUS_REPORT_BUDGET_BYTES`, each from {@link STATUS_REPORT_TRIMMABLE_MEMBERS}, each once, in
* any order, at least one. Absent means the operator trimmed nothing. An empty list, an unknown
* name or a repeated name is a `ZodError`, and because `StatusReportSchema` is strict end to end
* that fails the whole report. A name here and its member in the same report is incoherent; the
* operator omits every member it names.
*/
const StatusReportTruncatedSchema = z.array(z.enum(STATUS_REPORT_TRIMMABLE_MEMBERS)).min(1).max(STATUS_REPORT_TRIMMABLE_MEMBERS.length).refine((xs) => new Set(xs).size === xs.length, { message: "each trimmed member appears once" });
const StatusReportSchema = z.object({
	dataPlaneId: z.string().min(1),
	channelName: z.string().min(1).optional(),
	platformVersion: z.string().min(1),
	helmChartVersion: z.string().min(1),
	health: HealthSchema,
	services: z.record(z.string(), ServiceStatusSchema),
	apps: z.record(STATUS_REPORT_APP_KEY_SCHEMA, AppStatusSchema),
	lastMigration: MigrationStatusSchema.nullable(),
	operatorVersion: z.string().min(1),
	reportedAt: z.string().min(1),
	observability: ObservabilityStatusSchema,
	orgCount: z.number().int().nonnegative().optional(),
	envelopeErrors: z.array(EnvelopeErrorSchema).optional(),
	customerChannels: z.array(CustomerChannelDescriptorSchema).optional(),
	computeUnitInUseNodeTypes: z.array(z.object({
		nodeType: z.string().min(1),
		count: z.number().int().nonnegative()
	}).strict()).optional(),
	/**
	* apps-MCP v2 V-6 (seam S5) — container restarts the operator observed since
	* the previous report, deduped by (podUid, containerName, restartCountAfter).
	* release-engine persists them to `release.app_restart_events`; the
	* `get_app_restart_history` MCP tool reads that table. Omitted when empty, so a
	* report from a fleet with no restarts is BYTE-IDENTICAL to a pre-V-6 report
	* (SYS-020.2 — additive optional).
	*
	* ⛔ **`.max(POD_LIFECYCLE_MAX)` is an AVAILABILITY control, not tidiness** (R-30.1).
	* The body limit is the measured express default of 100 kB (§0.2(g)) and **401**
	* realistic (249 B) records fill it alone; the burst is reachable on `dp-app` from
	* ONE operator restart. A 413 kills the WHOLE report — live status, per-version
	* health, the node-pool demand signal and scale-event
	* capture — every cycle, and never reaches this schema. The producer caps and
	* CARRIES the remainder (§3.1) so the bound costs latency, never data.
	*
	* ⚠️ **This `.max()` bounds the COUNT, not the BYTES** — see
	* {@link POD_LIFECYCLE_MAX}, whose docblock carries the measured typical-vs-maximal
	* gap (249 B vs 943 B) and the reason the byte-level bound must live in the producer
	* (R-71). Do not read a green parse here as "the body fits".
	*/
	podLifecycle: z.array(PodLifecycleRecordSchema).max(200).optional(),
	/**
	* compute-classes D19/D24/D38 — per-UNIT compute resolution PROVENANCE for this cycle: for each
	* fixture service and worker the operator resolved, what class it used and where the class came
	* from, what multiplier applied and where that came from, the declared vector, the effective
	* vector, the machine type chosen, the resolver's notes, and — when it could not be placed — the
	* refusal code.
	*
	* ⭐ WHY THIS FIELD EXISTS AT ALL. Before it there was NO channel: a fixture that could not be
	* placed reported `replicaSet: 'progressing'` with `readyReplicas: 0`, indistinguishable from
	* "still rolling", and `get_app_diagnostics` reported a silently-undeployable app as fine. No
	* platform layer anywhere named the MANIFEST as the cause. This is that layer — and it carries
	* SUCCESS rows too, because "why is this pod on this machine" (D12) is a question about working
	* apps, not only broken ones.
	*
	* ⛔ `.max(COMPUTE_RESOLUTION_MAX)` IS A REFUSAL BACKSTOP, NOT A SECOND TRUNCATOR (D48(b)).
	* This is the CP-side bound on a DP-ASSERTED array that CP persists verbatim, and it is an
	* AVAILABILITY control — exactly the posture `podLifecycle` above already takes. ⚠️ It does NOT
	* truncate: an over-cap array is REJECTED, not trimmed, so D37's one-truncator rule survives
	* intact — the PRODUCER's slice is the only thing that ever drops a row, and in practice this
	* bound never trips because the producer sliced first. It exists for the case where the producer
	* is wrong.
	* ⛔ THE CONSEQUENCE IS REPORT-WIDE and must be stated where consumers read it: because
	* `StatusReportSchema` is `.strict()` end to end, an over-cap array fails the ENTIRE report, not
	* this field. That is the correct trade for a control channel — a DP that can force unbounded
	* CP-side persistence is the worse failure — but it is why the producer's slice is mandatory
	* rather than advisory.
	*
	* ⛔ AND THE COUNT BOUND IS STILL NOT A PROOF THAT THE BODY FITS. `COMPUTE_RESOLUTION_MAX`
	* (200, mirroring {@link POD_LIFECYCLE_MAX}) bounds ROWS, not BYTES: release-engine's ingest body
	* limit is the measured ~100 kB express default, a 413 kills the WHOLE report every cycle, and
	* zod cannot express "the encoded array is under N kB". The operator owns the BYTE budget and
	* sets {@link StatusReportSchema.shape.computeResolutionsTruncated} when either cap drops rows.
	*
	* OPTIONAL and OMITTED-WHEN-EMPTY (SYS-020 additive): a report from a DP that resolved nothing is
	* BYTE-IDENTICAL to a pre-change report, and an older operator that never emits it must still
	* parse under a new CP.
	*
	* ⛔ ORDERING — CP FIRST (D13). `StatusReportSchema` is `.strict()` and release-engine parses the
	* WHOLE report against it (`operator-api/dto/status-report.dto.ts`, under the global strict
	* pipe). An operator emitting this against an un-upgraded CP fails the ENTIRE report — live
	* status, per-version health, the node-pool demand signal — every cycle,
	* for every app on that DP. Ship the CP that accepts this BEFORE the DP that emits it.
	*
	* ⛔ security-audit §5 / D32 — every row carries `appId` (the app SLUG) so the CP diagnostics
	* section can filter `r.appId === slug` SERVER-SIDE. `fixtureId` alone is NOT unique across apps
	* (`dp_fixture_status`'s PK proves it), so without that filter an app-scoped route hands app A's
	* owner app B's fixtures, vectors and SKUs — the section built to satisfy §5 failing §5.
	*/
	computeResolutions: z.array(computeResolutionSchema).max(COMPUTE_RESOLUTION_MAX).optional(),
	/**
	* compute-classes D37 — TRUE iff the operator's count or byte cap DROPPED rows from
	* `computeResolutions` on this cycle.
	*
	* ⭐ TRUNCATION IS A WIRE FACT AND MUST BE STATED AT THE SOURCE, because only the producer knows
	* it capped. ⛔ A consumer MUST NOT infer it from `items.length === COMPUTE_RESOLUTION_MAX`: the
	* per-app filtered list is almost never the capped DP-wide list, so that test is wrong in BOTH
	* directions — false on a truncated report, and true on an untruncated 200-unit app.
	*
	* OPTIONAL: absent means "not reported", which for a pre-change operator is the honest answer and
	* is NOT the same as `false`. A consumer renders absent as unknown-completeness, not as complete.
	*
	* ⛔ SHIPS IN THE SAME CP RELEASE AS `computeResolutions` — D42(c)(3). Automatic here (one
	* commit, one schema, one changeset), and stated so no future cherry-pick or revert splits them:
	* the array WITHOUT this flag is a surface that cannot distinguish "the operator reported
	* everything" from "the operator capped and you are looking at a truncated list presented as
	* complete", and the only consumer defence against that — `items.length === cap` — is wrong in
	* both directions (see the docblock above). A truncation flag arriving one release after the data
	* it qualifies is worse than either shipping together, because the intervening release renders a
	* confident-looking partial answer.
	*/
	computeResolutionsTruncated: z.boolean().optional(),
	livePools: z.array(liveNodePoolSchema).optional(),
	/**
	* D19 (R-25/R-26/R-42) — the SIBLING of {@link livePools}: **demand this data plane could not
	* satisfy at observation time — including node types that HAVE live nodes but still have
	* unschedulable pods.** See `@zion/sdk-contracts`' `unsatisfied-demand.ts` for the full rationale.
	*
	* ⛔ NOT "node types that never obtained a node", and the producer MUST NOT filter this against its
	* own census (R-26). `livePools` rides the same report, so a consumer joins the two on `nodeType`
	* itself; a producer-side filter would silently drop the quota-blocked state. `A-319`/`A-320` gate it.
	*
	* ⛔ WHY A SIBLING AND NOT A FIELD ON `livePools` (R-25). A census row is *"every node sharing a
	* (role, nodeType) pair"* — an OBSERVATION — and a data plane that never got a node produces NO ROW,
	* so there is nothing to hang a field on. Synthesising a `nodeCount: 0` row is **forbidden**: it
	* would make an observed zero and an inferred zero indistinguishable *inside the census*, which is
	* the confusion D19 exists to remove, one layer down. ⚠️ The working attribution source is per-POD
	* (`spec.nodeSelector['zion.witness.ai/node-type']`), so this aggregate has no `role` and no relation
	* to any observed node — keying it by `nodeType` alone avoids inventing both. **`A-318`'s last arm
	* asserts a `demand` key on a census row is REJECTED**, so the withdrawn mechanism cannot return.
	*
	* ⛔ THREE STATES, POSITIONAL: **absent** ⇒ not measured · **`[]`** ⇒ looked, nothing unsatisfied ·
	* **entries** ⇒ these types could not be satisfied. `.optional()` is what makes absent-vs-`[]`
	* expressible, so it is load-bearing rather than a back-compat convenience. ⛔ Never `.default([])`.
	*
	* ⛔ `.max(UNSATISFIED_DEMAND_MAX)` IS AN AVAILABILITY CONTROL, NOT TIDINESS — the posture
	* `podLifecycle` and `computeResolutions` already take. An over-cap array is REJECTED, not trimmed,
	* and because this schema is `.strict()` end to end that rejection fails the ENTIRE report. That is
	* the correct trade for a control channel — a DP that can force unbounded CP-side persistence is the
	* worse failure — but it is why the producer's slice is mandatory rather than advisory.
	*
	* ⛔ ORDERING — CP FIRST (D6/D13), same as `computeResolutions`. release-engine parses the WHOLE
	* report under the global strict pipe, so an operator emitting this against an un-upgraded CP fails
	* live status, per-version health and the node-pool demand signal — every
	* cycle, for every app on that DP. Ship the CP that ACCEPTS before the DP that EMITS.
	*
	* ⚠️ WS-3 owns the persist column and its migration: `release.dp_live_status.live_pools` is a jsonb
	* column typed `LiveNodePool[]`, so a top-level sibling array cannot ride inside it.
	*/
	unsatisfiedDemand: z.array(unsatisfiedDemandSchema).max(UNSATISFIED_DEMAND_MAX).optional(),
	/**
	* app-pools WS-2. The compiled and clamped app-pool node groups, one per `(appKey, orgId,
	* workload, nodeType)` tuple (`security-audit.md` chain D hop 3). Persisted by release-engine
	* into `release.dp_live_status.app_pool_node_groups`. Recomputed by provisioning-svc, which
	* derives the CP-owned `diskType` and `maxPodsPerNode` from the clamped `nodeType`. It then
	* merge-PATCHes `spec.appPoolNodeGroups`, from which the Composition renders one node pool
	* per item.
	*
	* Three states, positional, and the consumer's guard is `!== undefined` and not `.length > 0`.
	* The same rule held for the removed `isolatedNodePools` member. Absent means the operator
	* has not completed a full reconcile, or predates the field, or runs on `provider: 'local'`
	* where no group is ever compiled. An empty array means it compiled and found zero pools, a
	* real instruction to delete them. Entries mean the compiled set. A collapse of absent into
	* empty reads a "haven't told you yet" operator as "the app removed its pools". Crossplane's
	* `deletionPolicy: Delete` then destroys live node pools with tenant attempts running on them,
	* on a one-minute cron, with no human action.
	*
	* The cap rides `appPoolNodeGroupsSchema` itself instead of a `.max()` here, so
	* `APP_POOL_NODE_GROUP_MAX` is stated once at its declaration. The refusal semantics match the
	* siblings; see {@link APP_POOL_REPORTED_STATUS_MAX}.
	*
	* Ordering is CP first (D6 and D13), as for `computeResolutions` and `unsatisfiedDemand`. This
	* schema parses the whole report under one strict pipe. An operator emitting this against an
	* un-upgraded CP therefore fails live status, per-version health and the demand signal, every
	* cycle and for every app on that DP. Ship the CP that accepts before the DP that emits.
	*/
	appPoolNodeGroups: appPoolNodeGroupsSchema.optional(),
	/**
	* app-pools WS-2. One row per declared pool: where its work can run, and the numbers in force.
	* release-engine persists it into `release.dp_live_status.app_pools` and renders it on the
	* Capacity screen's `app-pool` row.
	*
	* Absent means not reported, and it never means "no pools". An empty array is the different,
	* real answer: the operator looked and this data plane declares none. That is the `livePools`
	* distinction. A pool whose placement failed is reported with `placement: 'unschedulable'` and
	* is never omitted, because a silently missing row is the confident-zero defect in new clothes.
	*
	* CP first (D6 and D13), as for `appPoolNodeGroups` above.
	*/
	appPools: z.array(AppPoolReportedStatusSchema).max(200).optional(),
	/**
	* private-release-channels §2c, R-016. The operator's full snapshot of decisions on live offers
	* for its paired orgs, paged at {@link CHANNEL_SHARE_DECISIONS_MAX} per report. A decision absent
	* from a report keeps its stored state on the CP; the CP upserts and never deletes. Absent means
	* not reported; `[]` means the operator looked and found nothing to send.
	*
	* CP first (D6 and D13), as for `appPools` above: the CP that sends `channelOffersByOrg` is the
	* CP that parses this field, so an operator gates the field on having received that member.
	*/
	channelShareDecisions: z.array(ChannelShareDecisionSchema).max(20).optional(),
	/**
	* 2026-10-14-aws-dp-parity D3, D5. Each org's adoption state of each app on this data plane, at
	* most {@link APP_ADOPTIONS_PER_REPORT_MAX} rows per report; the operator pages the rest across
	* reports. One row per `(orgId, appId)` pair: a repeated pair is a `ZodError` at
	* `['appAdoptions', index, 'appId']`. Release-engine fences each row (see
	* {@link AppAdoptionReportSchema}) and upserts the kept ones on `(data plane, org, app)`.
	*
	* Absent means the operator predates the member or did not receive `appOffersByOrg`; the CP
	* then keeps today's app set for this data plane (D5). `[]` means the operator read core-api and
	* holds no unsent row. An event member (D-16 of the status report budget): it rides a delta as it
	* rides a full report, and no hash covers it. It is never trimmable, because an absent member
	* reads as "keep today's set".
	*
	* The page length is the pass signal (finding WS-1-R4). The rules:
	*
	* 1. The operator fills each page with unsent rows, in `orgId/appId` order, up to
	*    {@link APP_ADOPTIONS_PER_REPORT_MAX}. After each read of core-api it sends the page, `[]`
	*    included.
	* 2. So a page of exactly {@link APP_ADOPTIONS_PER_REPORT_MAX} rows means that more rows follow.
	* 3. A page of fewer rows, `[]` included, closes the pass. The CP then holds every row that the
	*    data plane held at that read.
	* 4. The CP counts the rows it received, before its fence drops any.
	* 5. The CP turns on the filter of the desired state only on the first report whose page closes
	*    the pass. The filter keeps `FIRST_PARTY_APPS` plus the apps that an org on the data plane
	*    accepted (D1).
	* 6. A full page before that report leaves today's app set (D5). The accepted rows of the later
	*    pages have not arrived, and a filter on a part of the set withdraws their apps.
	* 7. After the pass closes, the filter stays on, and a page of any length only upserts rows.
	*
	* A set of exactly 256 rows therefore needs two reports: the full page, then `[]`.
	*
	* A second reader (2026-10-13-blob-buckets R-060 item 2): release-engine's blob bucket
	* allocator reads the stored rows with `state = 'accepted'` from `release.dp_app_adoptions`,
	* keyed by CP org id. It reads a data plane as unobserved, and moves no binding, while that
	* data plane has no `dp_live_status` row or a NULL `app_adoptions_reported_at`. This member is
	* the one adoption report; no second member carries adoptions.
	*
	* CP first (D6 and D13), as for `channelShareDecisions`: the CP that sends `appOffersByOrg` is
	* the CP that parses this field, so the operator sends it only after it received that member.
	*/
	appAdoptions: z.array(AppAdoptionReportSchema).max(256).superRefine((rows, ctx) => {
		const seen = /* @__PURE__ */ new Set();
		rows.forEach((row, i) => {
			const pair = `${row.orgId}/${row.appId}`;
			if (seen.has(pair)) ctx.addIssue({
				code: "custom",
				path: [i, "appId"],
				message: "duplicate (orgId, appId) pair"
			});
			seen.add(pair);
		});
	}).optional(),
	/**
	* 2026-10-13-blob-buckets R-069 item 1, R-071 items 1 and 8. Each org's settings of each
	* allocatable bucket of each accepted app on this data plane, at most
	* {@link BLOB_BUCKET_SETTINGS_PER_REPORT_MAX} rows per report; the operator program pages the
	* rest across reports. One row per `(orgId, appId, logical)` triple
	* ({@link blobBucketSettingsKey}): a repeated triple is a `ZodError` at
	* `['blobBucketSettings', index, 'logical']`. Release-engine fences each row (see
	* {@link BlobBucketSettingsReportSchema}) and upserts the kept ones on `(data plane, org, app,
	* logical)` into `release.dp_org_blob_bucket_settings`, keeping the later `updatedAt` (WS-45).
	*
	* Absent means the operator predates the member, its last envelope carried no `orgDataLocations`,
	* or it got a 404 from a core-api that predates `GET /internal/v1/blob-bucket-settings` (R-071
	* item 5). `[]` means
	* the operator holds no unsent row. An absent member changes no stored row. An event member
	* (D-16): it rides a delta as it rides a full report, no hash covers it, and the operator never
	* trims it.
	*
	* No pass rule (R-071 item 8). core-api answers a row for every accepted adoption and declared
	* allocatable bucket, so a triple with no stored row means "not yet sent", and the allocator
	* creates no bucket for it (outcome `waiting-settings`). A page of any length only upserts.
	*
	* CP first (D6 and D13). The gate is `orgDataLocations`, a desired-state member of the same
	* stage: the CP that sends it is the CP that parses this field. So the operator sends this member
	* only while its last envelope carries a parsed `orgDataLocations`. `appOffersByOrg` is no gate
	* here: CPs from before this member send it, and their strict parse refuses the whole report
	* when this key is present. A CP rollback drops `orgDataLocations`, and the operator then stops.
	*/
	blobBucketSettings: z.array(BlobBucketSettingsReportSchema).max(256).superRefine(refineDistinctBlobBucketSettings).optional(),
	/**
	* 2026-10-13-blob-buckets R-089 item 2. The open org admin relocation requests of this data
	* plane that the operator program has not yet sent on a 2xx, at most
	* {@link BLOB_RELOCATION_REQUESTS_PER_REPORT_MAX} (64) rows per report; the operator program
	* pages the rest across reports, as it pages `blobBucketSettings`. Each `requestId` once: a
	* repeated id is a `ZodError` at `['blobRelocationRequests', index, 'requestId']`. Release-engine
	* fences each row (see {@link BlobRelocationRequestReportSchema}) and inserts the kept ones into
	* `release.blob_relocation_requests` with a conflict skip on `(dp_id, request_id)` (WS-21). The
	* provisioning-svc sweep then reads that table and creates the plan (WS-52).
	*
	* Absent means the operator predates the member, its last envelope carried no `orgDataLocations`,
	* or it got a 404 from a core-api that predates `GET /internal/v1/blob-relocation-requests`
	* (R-071 item 5). `[]` means the operator holds no unsent request. An absent member changes no
	* stored row. An event member (D-16): it rides a delta as it rides a full report, no hash covers
	* it, and the operator never trims it.
	*
	* No pass rule. The operator program keeps an in-memory ledger of the rows it sent on a 2xx. A
	* restart empties the ledger and sends every open request again, and the conflict skip of the CP
	* makes the repeat change nothing.
	*
	* CP first (D6 and D13). The gate is `orgDataLocations`, as for `blobBucketSettings`: stages 3b
	* and 4b of 2026-10-13-blob-buckets ship in the one PR #4170, so every CP that sends
	* `orgDataLocations` parses this field. `blobRelocationStates` is no gate: the CP omits it when it
	* holds no row, so its absence cannot tell an old CP from a new one.
	*/
	blobRelocationRequests: z.array(BlobRelocationRequestReportSchema).max(64).superRefine(refineDistinctBlobRelocationRequests).optional(),
	/**
	* private-release-channels R-048: the operator's capability names; the CP gates private pins on
	* `private-channels`. 2026-09-29-gpu-and-regional-app-pools R-063: release-engine ships a pool
	* carrying `region` only to a data plane whose last report names
	* {@link REGIONAL_POOLS_CAPABILITY}. 2026-10-13-blob-buckets R-104 item 4: release-engine
	* sends `claimCheckBindings` only to a data plane whose last report names
	* {@link BLOB_CLAIMCHECK_CAPABILITY}.
	*/
	capabilities: z.array(CapabilitySchema).max(16).optional(),
	/**
	* 2026-09-29-gpu-and-regional-app-pools D-19, R-005. One entry per satellite in
	* `desired.satellites`, each region once, at most {@link SATELLITE_STATUS_MAX}; see
	* {@link SatelliteStatusSchema}. Written by the home operator (WS-10) for the console's view of
	* each region. release-engine (WS-8) parses it with the rest of the report and persists
	* `report.satellites ?? null` into the nullable jsonb column `release.dp_live_status.satellites`
	* beside `live_pools` (R-021). site-admin-api's `GET /v1/data-planes/:id/satellites` (WS-6) joins
	* each entry's phase and observed counts onto the satellite it returns, and the console (WS-7)
	* shows them. Billing never reads this member; it reads the rows the home operator merges into
	* `livePools` (R-005).
	*
	* Absent means the operator predates satellites or received no `satellites` member in its
	* envelope; `[]` means the envelope enabled none. CP first (D6 and D13), as for `appPools`: an
	* operator emitting this against an un-upgraded CP fails the whole report.
	*/
	satellites: z.array(SatelliteStatusSchema).max(64).refine((xs) => new Set(xs.map((x) => x.region)).size === xs.length, { message: "each region appears once" }).optional(),
	/**
	* 2026-10-15-per-dp-door-identity D8. The state of the operator program's door-pull refresher:
	* the key it signs with, the expiry of its newest self-minted token, its last success, its last
	* failure and how many namespaces hold the newest token. See {@link DoorPullReportSchema}. A
	* header member (`STATUS_REPORT_HEADER_MEMBERS`). release-engine stores it for the reporting data
	* plane; provisioning-svc's `DoorPullTokenLoop` stops the bootstrap mint while
	* `isDoorPullSelfMintFresh` answers `true` for it. Absent means the operator predates the member
	* or its refresher is off. CP first, as for `appPools`: the report schema is strict, so the CP
	* that accepts the member ships before the operator program that sends it.
	*/
	doorPull: DoorPullReportSchema.optional(),
	/**
	* 2026-10-08-status-report-budget D-6. The etag map of this full report, computed by
	* `statusReportSectionEtags` over the report as sent. An operator that lists
	* `STATUS_DELTA_CAPABILITY` sends it on every full report, and the CP keeps it as the base
	* of the next delta (D-15). Absent means an operator that predates deltas.
	*
	* The CP never stores the claim unchecked. It computes the map from the body itself, and it
	* stores its own map. A claim that differs from it means two builds of the hash helper. The
	* CP then answers 2xx with `requestFullStatusReport` (`judgeStatusReportFull` in
	* `status-report-delta.ts`, finding WS-1-R1), and the operator sends its next report in full.
	*
	* Appended after `satellites`. No refine sits on this object, because release-engine's DTO
	* calls `.extend()` on it, and zod 4 refuses `.extend()` over a refined object.
	*/
	sectionEtags: StatusReportSectionEtagsSchema.optional(),
	/**
	* 2026-10-08-status-report-budget D-10. The members that the operator trimmed from this full
	* report to fit `STATUS_REPORT_BUDGET_BYTES`. See {@link StatusReportTruncatedSchema}.
	*/
	truncated: StatusReportTruncatedSchema.optional()
}).strict();
/** The delta object before its refine: the full shape, less the two meta members of a full report,
*  with the four required sections made optional, plus `baseEtag` and a required `sectionEtags`. */
function statusReportDeltaObjectSchema() {
	return StatusReportSchema.omit({
		sectionEtags: true,
		truncated: true
	}).partial({
		services: true,
		apps: true,
		lastMigration: true,
		observability: true
	}).extend({
		baseEtag: StatusReportEtagSchema,
		sectionEtags: StatusReportSectionEtagsSchema
	}).strict();
}
/** Whether every section body and every app body that a delta carries has an etag in its map. */
function deltaBodiesNameAnEtag(delta, ctx) {
	for (const section of STATUS_REPORT_SECTIONS) if (delta[section] !== void 0 && delta.sectionEtags[section] === void 0) ctx.addIssue({
		code: "custom",
		path: [section],
		message: `the body of ${section} has no etag in sectionEtags`
	});
	for (const app of Object.keys(delta.apps ?? {})) if (!Object.hasOwn(delta.sectionEtags.apps, app)) ctx.addIssue({
		code: "custom",
		path: ["apps", app],
		message: `the body of app ${app} has no etag in sectionEtags.apps`
	});
}
/**
* One delta status report (2026-10-08-status-report-budget D-6, D-7, D-8). The operator sends it
* in place of a full report when the desired-state envelope says the CP accepts deltas
* (`statusReportAccepts.delta`) and the operator holds a base that the CP accepted.
*
* - The header members ({@link STATUS_REPORT_HEADER_MEMBERS}) and the event members
*   ({@link STATUS_REPORT_EVENT_MEMBERS}) keep their full-report schema and meaning.
* - `baseEtag` is `statusReportEtag` of the etag map that the delta was built against. The CP
*   answers 409 with `requestFullStatusReport` when it differs from the etag of the map it holds.
* - `sectionEtags` is the complete etag map of the report the operator would send in full.
* - Each section and `apps` is optional. A section travels only when its etag differs from the
*   base. `apps` holds only the apps whose etag differs from the base. An app key absent from
*   `sectionEtags.apps` removes that app.
* - A delta with no changed section still posts every cycle (D-8): it carries the header alone.
* - `truncated` is absent: only a full report is trimmed (D-10).
*
* Strict. The parse refuses, with a path to the member:
*
* - any unknown key, a missing `baseEtag` or a missing `sectionEtags`;
* - a section body whose name has no key in `sectionEtags`;
* - an app body whose key has no entry in `sectionEtags.apps`.
*
* The parse cannot see the stored base, so it cannot see a changed etag with no body.
* `judgeStatusReportDelta` in `status-report-delta.ts` judges that, and the CP answers 400 for it.
* Pure.
*/
const StatusReportDeltaSchema = statusReportDeltaObjectSchema().superRefine(deltaBodiesNameAnEtag);
/**
* The body that `POST /v1/operator/data-planes/:dpId/status` accepts once the CP speaks deltas: a
* full report or a delta. A body with `baseEtag` can only parse as a delta, because the full arm is
* strict and has no such key. A body without it can only parse as a full report, because the delta
* arm requires it. So one body never matches both arms.
*
* No transform sits on either arm, so `createOpenApiDocument()` can represent the union. A body that
* matches neither arm is a `ZodError` with code `invalid_union`. Pure.
*/
const StatusReportBodySchema = z.union([StatusReportSchema, StatusReportDeltaSchema]);
//#endregion
export { APP_ADOPTIONS_PER_REPORT_MAX, APP_POOL_REPORTED_STATUS_MAX, APP_POOL_SATELLITE_MEMBERS_MAX, APP_POOL_TIERS_STATUS_MAX, APP_POOL_UNSCHEDULABLE_REASON_MAX_LENGTH, AppAdoptionReportSchema, AppPoolReportedStatusSchema, AppPoolTiersStatusListSchema, AppStatusSchema, BLOB_BUCKET_SETTINGS_PER_REPORT_MAX, BLOB_CLAIMCHECK_CAPABILITY, BLOB_RELOCATION_REQUESTS_PER_REPORT_MAX, BlobBucketSettingsReportSchema, BlobRelocationRequestReportSchema, CAPABILITY_PATTERN, CHANNEL_SHARE_DECISIONS_MAX, CapabilitySchema, ChannelShareDecisionSchema, EnvelopeErrorSchema, FixtureJobRunSchema, FixtureServiceStatusSchema, FixtureStatusSchema, FixtureVersionStatusSchema, MigrationStatusSchema, PER_VERSION_DECLARATIONS_CAPABILITY, POD_LIFECYCLE_MAX, PRIVATE_CHANNELS_CAPABILITY, PodLifecycleRecordSchema, REGIONAL_POOLS_CAPABILITY, SATELLITE_CENSUS_ROWS_MAX, SATELLITE_PHASES, SATELLITE_STATUS_MAX, SATELLITE_STATUS_STALE_AFTER_MS, STATUS_REPORT_CAPABILITIES_MAX, STATUS_REPORT_ETAG_PATTERN, STATUS_REPORT_EVENT_MEMBERS, STATUS_REPORT_HEADER_MEMBERS, STATUS_REPORT_REQUIRED_SECTIONS, STATUS_REPORT_SECTIONS, STATUS_REPORT_TRIMMABLE_MEMBERS, SatelliteMemberObservationSchema, SatelliteNodeGroupObservationSchema, SatelliteStatusMessageSchema, SatelliteStatusSchema, ServiceStatusSchema, StatusReportBodySchema, StatusReportDashboardSchema, StatusReportDeltaSchema, StatusReportEtagSchema, StatusReportSchema, StatusReportSectionEtagsSchema, StatusReportTruncatedSchema, blobBucketSettingsKey, refineDistinctBlobBucketSettings, refineDistinctBlobRelocationRequests };

//# sourceMappingURL=status-report.schemas.js.map